(() => {
  'use strict';

  /* =====================================================
     Perf: scheduler — différer le travail non-critique au TBT.
     Utiliser whenIdle() pour tout ce qui n'est PAS dans le LCP path
     (cookies, reveal animations, mobile sliders, etc.). Le navigateur
     peint d'abord, puis exécute pendant les frames idle.
  ===================================================== */
  window.whenIdle = window.whenIdle || function (fn, opts) {
    if (typeof requestIdleCallback === 'function') {
      return requestIdleCallback(fn, opts || { timeout: 2000 });
    }
    return setTimeout(fn, 1);
  };

  /* =====================================================
     Helpers DOM
  ===================================================== */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const rootEl = document.documentElement;

  /* Base path: works both at the domain root and in a staging subdirectory */
  const __BASE = (() => {
    const m = location.pathname.match(/^(\/staffr\/)/i);
    return m ? m[1] : '/';
  })();
  window.__BASE_PATH = __BASE;

  // Compare deux assets en ignorant l'origine et les querystrings
  function sameAsset(currentSrc, wantedPath) {
    if (!currentSrc || !wantedPath) return false;
    try {
      const cur = new URL(currentSrc, location.href).pathname;
      const want = new URL(wantedPath, location.href).pathname;
      return cur.endsWith(want);
    } catch {
      return currentSrc.includes(wantedPath);
    }
  }

  function escapeHtml(value = '') {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  }

  /* =====================================================
     Menu desktop (petit burger utilitaire)
     -> Event delegation pour gérer le header en partial
  ===================================================== */
  document.addEventListener('click', (e) => {
    const menuBtn = e.target.closest('.menu-btn');
    if (!menuBtn) return;

    const navLinks = document.getElementById('navLinks') || document.getElementById('NavLinks');
    const mnavToggle = document.getElementById('mnav-toggle');
    if (mnavToggle) return; // si menu fullscreen actif, on laisse sa logique

    navLinks?.classList.toggle('show');
    const expanded = menuBtn.getAttribute('aria-expanded') === 'true';
    menuBtn.setAttribute('aria-expanded', String(!expanded));
  });

  // Smooth scroll + fermeture du menu mobile (event delegation)
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;

    const href = a.getAttribute('href') || '';
    if (href.startsWith('#/')) return; // laisser le router gérer

    const id = href.slice(1);
    const el = document.getElementById(id);
    if (!el) return;

    e.preventDefault();

    // Sur mobile, on descend un peu plus bas pour révéler le contenu
    // actionnable (CTA, toggle, store badges) plutôt que de s'arrêter
    // pile sur l'en-tête de section.
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    // Accueil : la barre audience s'ajoute sous l'en-tête dès qu'on quitte le Hero.
    // --audience-stack = bas réel de la barre ; le début du contenu de la section
    // (son titre compris) s'aligne juste dessous, sans le padding vide au-dessus.
    const stack = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--audience-stack'));
    if (isMobile && stack && el.id !== 'top') {
      const contentTop = el.getBoundingClientRect().top + window.pageYOffset
        + (parseFloat(getComputedStyle(el).paddingTop) || 0);
      window.scrollTo({ top: contentTop - stack - 12, behavior: 'smooth' });
    } else if (isMobile) {
      const headerH = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      ) || 64;
      const extra = 90; // décale la cible plus bas dans la page
      const top = el.getBoundingClientRect().top + window.pageYOffset - headerH + extra;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const navLinks = document.getElementById('navLinks') || document.getElementById('NavLinks');
    const menuBtn = document.querySelector('.menu-btn');
    navLinks?.classList.remove('show');
    menuBtn?.setAttribute('aria-expanded', 'false');
  });

  /* =====================================================
     THEME — API centralisée (desktop + mobile)
  ===================================================== */
  function getTheme() {
    // 1) On privilégie toujours le choix utilisateur enregistré
    try {
      const stored = localStorage.getItem('theme');
      if (stored === 'light' || stored === 'dark') return stored;
    } catch { }

    // 2) Sinon on lit l'attribut HTML (utile au tout premier chargement)
    const attr = rootEl.getAttribute('data-theme');
    if (attr === 'light' || attr === 'dark') {
      return attr;
    }

    // 3) Fallback
    return 'light';
  }

  function setTheme(mode) {
    const m = (mode === 'light' ? 'light' : 'dark');
    rootEl.setAttribute('data-theme', m);
    try { localStorage.setItem('theme', m); } catch { }
    const themeInput = $('#mnav-theme');
    if (themeInput) themeInput.checked = (m === 'dark');
    document.dispatchEvent(new CustomEvent('theme:change', { detail: { theme: m } }));
  }

  // Boot initial : on se base toujours sur getTheme() (donc localStorage d'abord)
  (function bootTheme() {
    const initial = getTheme();
    setTheme(initial);
  })();

  // Synchronisation de l’icône du bouton thème (header)
  function syncThemeIcon() {
    const btn = $('#themeToggle');
    if (!btn) return;
    const isLight = getTheme() === 'light';
    btn.textContent = isLight ? '🌙' : '🌞';
    btn.setAttribute('aria-label', isLight ? 'Passer au mode sombre' : 'Passer au mode clair');
  }
  // Exposer pour usage depuis d'autres IIFE (notamment afterHeaderFooterLoad).
  window.syncThemeIcon = syncThemeIcon;
  syncThemeIcon();

  // Event delegation pour le bouton #themeToggle (header partial ou inline)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#themeToggle');
    if (!btn) return;
    setTheme(getTheme() === 'dark' ? 'light' : 'dark');
    syncThemeIcon();
  });

  const mnavThemeInput = $('#mnav-theme');
  if (mnavThemeInput) {
    mnavThemeInput.checked = (getTheme() === 'dark');
    mnavThemeInput.addEventListener('change', () => {
      setTheme(mnavThemeInput.checked ? 'dark' : 'light');
      syncThemeIcon();
    });
  }

  function bindMobileThemeToggle() {
    const mnavThemeInput = $('#mnav-theme');
    if (!mnavThemeInput || mnavThemeInput.dataset.themeBound === 'true') {
      if (mnavThemeInput) mnavThemeInput.checked = (getTheme() === 'dark');
      return;
    }

    mnavThemeInput.dataset.themeBound = 'true';
    mnavThemeInput.checked = (getTheme() === 'dark');
    mnavThemeInput.addEventListener('change', () => {
      setTheme(mnavThemeInput.checked ? 'dark' : 'light');
      syncThemeIcon();
    });
  }

  window.bindMobileThemeToggle = bindMobileThemeToggle;
  bindMobileThemeToggle();

  new MutationObserver(syncThemeIcon).observe(rootEl, { attributes: true, attributeFilter: ['data-theme'] });

  /* =====================================================
     i18n — API centralisée (desktop + mobile)
  ===================================================== */
  const base = __BASE;
  window.I18N = {
    lang: 'fr',
    dict: {},
    base,
    async load(lang) {
      const url = this.base + 'i18n/' + lang + '.json?v=14';
      // Cache par défaut : le ?v= cache-buster invalide à chaque release.
      // Le browser garde le JSON 1 h (configuré dans .htaccess) puis revalide.
      const res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      this.dict = await res.json();
      this.lang = lang;
      try { localStorage.setItem('lang', lang); } catch { }
      rootEl.lang = lang;
      rootEl.setAttribute('data-lang', lang);
      applyI18n();

      // Sections dépendantes
      renderHero(currentHeroMode());
      renderFeatureShowcase();
      setHowMode(currentHeroMode());
      setFeat(currentHeroMode());
      setDl(currentHeroMode());

      // Events (compat double nom)
      document.dispatchEvent(new CustomEvent('i18n:change', { detail: { lang } }));
      window.dispatchEvent(new CustomEvent('i18n:changed', { detail: { lang } }));
    },
    t(path, fb = '') { return path.split('.').reduce((o, k) => o?.[k], this.dict) ?? fb; }
  };

  function getLang() {
    // 1) Priorité au choix persisté par l'utilisateur
    try {
      const stored = localStorage.getItem('lang');
      if (stored === 'en' || stored === 'fr') return stored;
    } catch { }
    // 2) Puis l'attribut HTML
    const a = rootEl.getAttribute('data-lang') || rootEl.lang;
    if (a) return a.toLowerCase().startsWith('en') ? 'en' : 'fr';
    return 'fr';
  }
  async function setLang(lang) {
    const code = (lang === 'en' ? 'en' : 'fr');
    await I18N.load(code);
    const langBadge = $('#langToggle');
    if (langBadge) langBadge.textContent = (code === 'fr' ? 'EN' : 'FR');
    $$('.mnav__lang-btn').forEach(btn => {
      const on = btn.dataset.lang === code;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // MàJ labels unifiés
    const candLabel = I18N.t('hero.toggle.cand', 'Candidat');
    const cliLabel = I18N.t('hero.toggle.cli', 'Entreprise');
    const _curMode = currentHeroMode() === 'cli' ? cliLabel : candLabel;
    if (switchLabel) switchLabel.textContent = _curMode;
    if (featSwitchLabel) featSwitchLabel.textContent = _curMode;
    if (appsSwitchLabel) appsSwitchLabel.textContent = _curMode;
    if (howSwitchLabel) howSwitchLabel.textContent = _curMode;
    const _faqSL = document.getElementById('faqSwitchLabel');
    if (_faqSL) _faqSL.textContent = _curMode;
    const _mnavSL = document.getElementById('mnavSwitchLabel');
    if (_mnavSL) _mnavSL.textContent = _curMode;
  }
  window.setLang = setLang;

  /* -----------------------------------------------------
     Mini-sanitizer pour les chaînes i18n contenant du HTML.
     Whitelist stricte d'éléments et d'attributs, parse via
     DOMParser (sandbox, ne déclenche pas <script>/onerror).
     Tout ce qui n'est pas listé est supprimé.
  ----------------------------------------------------- */
  const I18N_ALLOWED_TAGS = new Set(['STRONG', 'EM', 'B', 'I', 'BR', 'SPAN', 'A', 'SMALL']);
  const I18N_ALLOWED_ATTRS = {
    A: new Set(['href', 'target', 'rel', 'class']),
    SPAN: new Set(['class']),
    SMALL: new Set(['class']),
    STRONG: new Set(['class']),
    EM: new Set(['class']),
    B: new Set(['class']),
    I: new Set(['class']),
    BR: new Set([])
  };
  const I18N_SAFE_URL = /^(https?:|mailto:|tel:|#|\/|\.\/|\.\.\/)/i;

  function sanitizeI18nHtml(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = String(html);
    const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_ELEMENT);
    const toRemove = [];
    let node = walker.nextNode();
    while (node) {
      const tag = node.tagName;
      if (!I18N_ALLOWED_TAGS.has(tag)) {
        toRemove.push(node);
      } else {
        const allowed = I18N_ALLOWED_ATTRS[tag] || new Set();
        [...node.attributes].forEach(attr => {
          const name = attr.name.toLowerCase();
          if (!allowed.has(name)) {
            node.removeAttribute(attr.name);
            return;
          }
          if (name === 'href' && !I18N_SAFE_URL.test(attr.value.trim())) {
            node.removeAttribute(attr.name);
          }
          if (name === 'target' && attr.value === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
          }
        });
      }
      node = walker.nextNode();
    }
    toRemove.forEach(n => {
      while (n.firstChild) n.parentNode.insertBefore(n.firstChild, n);
      n.parentNode.removeChild(n);
    });
    return tpl.content;
  }

  function setI18nValue(el, val) {
    if (val == null) return;
    const allowHtml = el.hasAttribute('data-i18n-html') || /<[a-z][\s\S]*?>/i.test(val);
    if (!allowHtml) {
      el.textContent = val;
      return;
    }
    const fragment = sanitizeI18nHtml(val);
    el.replaceChildren(fragment);
  }

  function applyI18n() {
    document.querySelectorAll('[data-i18n]').forEach(el => {
      const key = el.getAttribute('data-i18n');
      const val = I18N.t(key, null);
      setI18nValue(el, val);
    });

    document.querySelectorAll('[data-i18n-attr]').forEach(el => {
      const spec = el.getAttribute('data-i18n-attr');
      if (!spec) return;
      spec.split(';').forEach(pair => {
        const [attr, key] = pair.split(':').map(s => s?.trim()).filter(Boolean);
        if (!attr || !key) return;
        const val = I18N.t(key, null);
        if (val != null) el.setAttribute(attr, val);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', async () => {
    await setLang(getLang());
    $('#langToggle')?.addEventListener('click', () => setLang(I18N.lang === 'fr' ? 'en' : 'fr'));
  });
  document.addEventListener('click', (e) => {
    const desktopLangBtn = e.target.closest('#langToggle');
    if (desktopLangBtn) {
      const target = I18N.lang === 'fr' ? 'en' : 'fr';
      setLang(target).catch(err => console.error('Language switch failed:', err));
      return;
    }

    const btn = e.target.closest('.mnav__lang-btn');
    if (!btn) return;
    const target = btn.dataset.lang === 'en' ? 'en' : 'fr';
    setLang(target).catch(err => console.error('Language switch failed:', err));
  });

  /* =====================================================
     Menu mobile fullscreen — fermeture auto au clic
  ===================================================== */
  const mnavToggle = $('#mnav-toggle');
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.mnav__link, .mnav__cta');
    if (!link) return;
    if (mnavToggle && mnavToggle.checked) mnavToggle.checked = false;
  });

  /* =====================================================
     Sélecteurs HERO/FEATURES/HOW + APPS (DOWNLOAD)
  ===================================================== */
  const heroSwitch = $('#heroToggle');
  const switchLabel = $('#switchSingleLabel');
  const knobInner = heroSwitch?.querySelector('.knob-inner');

  // Titre, sous-titre et CTA principal du Hero : variantes [data-audience-only]
  // dans le HTML, affichées par le CSS selon html[data-mode] (pas de réécriture JS).
  const heroPhone = $('#heroPhone');
  const heroSection = document.querySelector('.hero');
  const heroSecondaryCta = $('#heroSecondaryCta');
  const midPageCtaTitle = $('#midPageCtaTitle');
  const midPageCtaText = $('#midPageCtaText');
  const midPagePrimaryCta = $('#midPagePrimaryCta');
  const midPageCtaEyebrow = document.querySelector('.mission-cta__eyebrow');

  // Features panes + toggle
  const gridCand = $('#gridCand');
  const gridCli = $('#gridCli');

  const featModeToggle = $('#featModeToggle')
    || $('#featuresModeToggle')
    || $('#featuresToggle');

  const featSwitchLabel = $('#featSwitchLabel')
    || $('#featuresSwitchLabel');

  const featKnobInner = featModeToggle?.querySelector('.knob-inner');
  let featRot = 0;
  const featureChoicesCand = $('#featureChoicesCand');
  const featureChoicesCli = $('#featureChoicesCli');
  const featureStageCand = $('#featureStageCand');
  const featureStageCli = $('#featureStageCli');
  const featureShowcaseState = { cand: 0, cli: 0 };
  const FEATURE_SHOWCASE_MEDIA = {
    cand: [
      { assetFr: 'assets/profile-app-fr.webp', assetEn: 'assets/profile-app-en.webp', shell: 'phone', kind: 'profile' },
      { assetFr: 'assets/map-app-fr.webp', assetEn: 'assets/map-app-en.webp', shell: 'phone', kind: 'map' },
      { assetFr: 'assets/list-app-fr.webp', assetEn: 'assets/list-app-en.webp', shell: 'phone', kind: 'contracts' },
      { assetFr: 'assets/cal-app-fr.webp', assetEn: 'assets/cal-app-en.webp', shell: 'phone', kind: 'calendar' },
      { assetFr: 'assets/facture-app-fr.webp', assetEn: 'assets/facture-app-en.webp', shell: 'phone', kind: 'payments' }
    ],
    cli: [
      { assetFr: 'assets/publier un besoin-fr.webp', assetEn: 'assets/publier un besoin-en.webp', shell: 'desktop', kind: 'post' },
      { assetFr: 'assets/Suivi de candidature-fr.webp', assetEn: 'assets/Suivi de candidature-en.webp', shell: 'desktop', kind: 'applications' },
      { assetFr: 'assets/confirmation de contrat - punch-fr.webp', assetEn: 'assets/confirmation de contrat - punch-en.webp', shell: 'desktop', kind: 'hours' },
      { assetFr: 'assets/facturation et suivis-fr.webp', assetEn: 'assets/facturation et suivis-en.webp', shell: 'desktop', kind: 'followups' }
    ]
  };

  // DOWNLOAD / APPS
  const appsModeToggle = $('#appsModeToggle');
  const appsSwitchLabel = $('#appsSwitchLabel');
  const appsLeftCand = $('#appsLeftCand');
  const appsLeftCli = $('#appsLeftCli');
  const appsPhone = $('#appsPhone');

  // HOW
  const howModeToggle = $('#howModeToggle');
  const howSwitchLabel = $('#howSwitchLabel');
  const howCopyCand = $('#howCopyCand');
  const howCopyCli = $('#howCopyCli');
  const howBenefitsCand = $('#howBenefitsCand');
  const howBenefitsCli = $('#howBenefitsCli');
  const howVisualCand = $('#howVisualCand');
  const howVisualCli = $('#howVisualCli');
  const howFlowCand = $('#howFlowCand');
  const howFlowCli = $('#howFlowCli');
  let howRot = 0;

  /* =====================================================
     Mobile Menu Toggle — Candidat / Client
     Shares global state with heroSwitch via modechange
  ===================================================== */
  let mnavRot = 0;

  function syncMnavUI(mode) {
    const mnavModeToggle = $('#mnavModeToggle');
    const mnavSwitchLabel = $('#mnavSwitchLabel');
    const mnavKnobInner = mnavModeToggle?.querySelector('.knob-inner');
    const isCli = (mode === 'cli');
    mnavModeToggle?.setAttribute('data-mode', isCli ? 'cli' : 'cand');
    mnavModeToggle?.setAttribute('aria-checked', String(isCli));
    if (mnavSwitchLabel) mnavSwitchLabel.textContent = labelFor(isCli ? 'cli' : 'cand');
    mnavRot += isCli ? 360 : -360;
    mnavKnobInner?.style.setProperty('--rot', mnavRot + 'deg');
  }

  // When modechange fires (from hero or elsewhere) → update mobile menu toggle
  document.addEventListener('modechange', (e) => {
    syncMnavUI(e.detail?.mode || 'cand');
  });

  function bindMobileNavToggle() {
    const mnavModeToggle = $('#mnavModeToggle');
    if (!mnavModeToggle) return;

    mnavModeToggle.setAttribute('role', 'switch');
    mnavModeToggle.setAttribute('tabindex', '0');
    window.__wireModeToggle?.(mnavModeToggle);
    syncMnavUI(document.documentElement.dataset.mode === 'cli' ? 'cli' : 'cand');
  }

  window.bindMobileNavToggle = bindMobileNavToggle;

  // Mobile menu toggle interaction is handled by wireToggle (applied to all
  // .mini-switch elements), which provides click, keyboard, and drag support.
  // No additional listeners are needed here — adding them would cause setHero
  // to fire twice per tap, making the toggle appear unresponsive.

  function getLandingCtaCopy(mode) {
    const normalized = mode === 'cli' ? 'cli' : 'cand';
    return I18N.t(`landingCta.${normalized}`, null) || {};
  }

  function updateLandingCtas(mode) {
    const normalized = mode === 'cli' ? 'cli' : 'cand';
    const copy = getLandingCtaCopy(normalized);
    const primaryHref = normalized === 'cli' ? 'https://app.staffrenter.com/client' : '#apps';
    const secondaryHref = '#mission';

    if (heroSecondaryCta) {
      if (copy.heroSecondary) heroSecondaryCta.textContent = copy.heroSecondary;
      heroSecondaryCta.href = secondaryHref;
      heroSecondaryCta.setAttribute('data-i18n', `landingCta.${normalized}.heroSecondary`);
    }
    if (midPageCtaEyebrow) {
      if (copy.midEyebrow) midPageCtaEyebrow.textContent = copy.midEyebrow;
      midPageCtaEyebrow.setAttribute('data-i18n', `landingCta.${normalized}.midEyebrow`);
    }
    if (midPageCtaTitle) {
      if (copy.midTitle) midPageCtaTitle.textContent = copy.midTitle;
      midPageCtaTitle.setAttribute('data-i18n', `landingCta.${normalized}.midTitle`);
    }
    if (midPageCtaText) {
      if (copy.midText) midPageCtaText.textContent = copy.midText;
      midPageCtaText.setAttribute('data-i18n', `landingCta.${normalized}.midText`);
    }
    if (midPagePrimaryCta) {
      if (copy.midPrimary) midPagePrimaryCta.textContent = copy.midPrimary;
      midPagePrimaryCta.href = primaryHref;
      midPagePrimaryCta.setAttribute('data-i18n', `landingCta.${normalized}.midPrimary`);
    }

    // Sticky mini-CTA mobile (audience-aware) — texte = hero primary, href = même route
    const mCtaStickLink = document.getElementById('mCtaStickLink');
    if (mCtaStickLink) {
      if (copy.heroPrimary) mCtaStickLink.textContent = copy.heroPrimary;
      mCtaStickLink.href = primaryHref;
      mCtaStickLink.setAttribute('data-i18n', `landingCta.${normalized}.heroPrimary`);
    }

    // Drawer mobile : signup toujours visible ; login UNIQUEMENT en mode client
    // (côté candidat l'app est le canal d'accès, pas un login web).
    //   • Signup candidat → #apps (anchor : section App Store / Play Store)
    //   • Signup client   → portail client direct
    //   • Login  client   → portail client (visible seulement en mode cli)
    const mnavSignupCta = document.getElementById('mnavSignupCta');
    if (mnavSignupCta) {
      mnavSignupCta.href = (normalized === 'cli') ? 'https://app.staffrenter.com/client' : '#apps';
    }
    const mnavLoginCta = document.getElementById('mnavLoginCta');
    if (mnavLoginCta) {
      const showLogin = (normalized === 'cli');
      mnavLoginCta.hidden = !showLogin;
      if (showLogin) {
        mnavLoginCta.href = 'https://app.staffrenter.com/client';
      }
    }

    // Header desktop : login button visible uniquement en mode client
    // (.hide-mobile garantit qu'il reste invisible <768px, le drawer gère le mobile).
    const hdrLoginCta = document.getElementById('hdrLoginCta');
    if (hdrLoginCta) {
      const showHdrLogin = (normalized === 'cli');
      hdrLoginCta.hidden = !showHdrLogin;
      if (showHdrLogin) {
        hdrLoginCta.href = 'https://app.staffrenter.com/client';
      }
    }

    // Final CTA section (closing-the-deal, avant footer) — copy + href
    // dédiés via i18n key finalCta.{cand|cli}.{eyebrow|title|text|primary|secondary}.
    const finalCopy = I18N.t(`finalCta.${normalized}`, null) || {};
    const finalCtaEyebrow = document.getElementById('finalCtaEyebrow');
    if (finalCtaEyebrow) {
      if (finalCopy.eyebrow) finalCtaEyebrow.textContent = finalCopy.eyebrow;
      finalCtaEyebrow.setAttribute('data-i18n', `finalCta.${normalized}.eyebrow`);
    }
    const finalCtaTitle = document.getElementById('finalCtaTitle');
    if (finalCtaTitle) {
      if (finalCopy.title) finalCtaTitle.textContent = finalCopy.title;
      finalCtaTitle.setAttribute('data-i18n', `finalCta.${normalized}.title`);
    }
    const finalCtaText = document.getElementById('finalCtaText');
    if (finalCtaText) {
      if (finalCopy.text) finalCtaText.textContent = finalCopy.text;
      finalCtaText.setAttribute('data-i18n', `finalCta.${normalized}.text`);
    }
    const finalCtaPrimary = document.getElementById('finalCtaPrimary');
    if (finalCtaPrimary) {
      if (finalCopy.primary) finalCtaPrimary.textContent = finalCopy.primary;
      finalCtaPrimary.href = primaryHref;
      finalCtaPrimary.setAttribute('data-i18n', `finalCta.${normalized}.primary`);
    }
    const finalCtaSecondary = document.getElementById('finalCtaSecondary');
    if (finalCtaSecondary) {
      const textSpan = finalCtaSecondary.querySelector('.final-cta__secondary-text');
      if (textSpan && finalCopy.secondary) textSpan.textContent = finalCopy.secondary;
      finalCtaSecondary.setAttribute('data-i18n', `finalCta.${normalized}.secondary`);
    }
  }

  /* =====================================================
     HOW — state helpers
  ===================================================== */
  function setHowBlockState(el, show) {
    if (!el) return;
    el.hidden = !show;
    el.classList.toggle('hidden', !show);
  }

  /* =====================================================
     UI sync helpers
  ===================================================== */
  function labelFor(mode) {
    return (mode === 'cli')
      ? (I18N.t('hero.toggle.cli', 'Entreprise'))
      : (I18N.t('hero.toggle.cand', 'Candidat'));
  }

  function syncHowSwitchUI(mode) {
    const isCli = mode === 'cli';
    howModeToggle?.setAttribute('data-mode', isCli ? 'cli' : 'cand');
    howModeToggle?.setAttribute('aria-checked', String(isCli));
    if (howSwitchLabel) howSwitchLabel.textContent = labelFor(isCli ? 'cli' : 'cand');
    howRot += isCli ? 360 : -360;
    howModeToggle?.querySelector('.knob-inner')?.style.setProperty('--rot', howRot + 'deg');
  }

  function syncFeatSwitchUI(mode) {
    const isCli = mode === 'cli';
    featModeToggle?.setAttribute('data-mode', isCli ? 'cli' : 'cand');
    featModeToggle?.setAttribute('aria-checked', String(isCli));
    if (featSwitchLabel) featSwitchLabel.textContent = labelFor(isCli ? 'cli' : 'cand');
    featRot += isCli ? 360 : -360;
    featKnobInner?.style.setProperty('--rot', featRot + 'deg');
  }

  function syncAppsSwitchUI(mode) {
    const isCli = (mode === 'cli');
    appsModeToggle?.setAttribute('data-mode', isCli ? 'cli' : 'cand');
    appsModeToggle?.setAttribute('aria-checked', String(isCli));
    if (appsSwitchLabel) appsSwitchLabel.textContent = labelFor(isCli ? 'cli' : 'cand');
    const knob = appsModeToggle?.querySelector('.knob-inner');
    const cur = parseFloat(knob?.style.getPropertyValue('--rot') || '0') || 0;
    knob?.style.setProperty('--rot', (cur + (isCli ? 360 : -360)) + 'deg');
  }

  /* =====================================================
     Mode initial (audience)
     Source de vérité unique : html[data-mode] = 'cli' | 'cand'.
     Préférence mémorisée : localStorage.heroMode = 'client' | 'candidat',
     écrite uniquement sur un choix explicite (jamais au simple chargement).
     ⚠️ Même normalisation que le script d'audience du <head> d'index.html.
  ===================================================== */
  function normalizeAudience(value) {
    const v = String(value || '').toLowerCase();
    if (v === 'client' || v === 'cli') return 'cli';
    if (v === 'candidate' || v === 'candidat' || v === 'cand') return 'cand';
    return null;
  }

  function getSavedAudience() {
    try { return normalizeAudience(localStorage.getItem('heroMode')); } catch { return null; }
  }

  // Priorité : ?audience= > ?mode= (historique) > #mode= > préférence > candidat.
  function getInitialAudience() {
    let fromUrl = null;
    try {
      const params = new URLSearchParams(location.search);
      fromUrl = normalizeAudience(params.get('audience'))
        || normalizeAudience(params.get('mode'))
        || normalizeAudience(location.hash.match(/mode=(candidat|client)/i)?.[1]);
    } catch { }
    if (fromUrl) return { mode: fromUrl, source: 'url' };
    const saved = getSavedAudience();
    if (saved) return { mode: saved, source: 'saved' };
    return { mode: 'cand', source: 'default' };
  }

  // Une fois appliqué, le paramètre d'audience est retiré de l'URL : un rechargement
  // ou un partage ne force plus une audience que le visiteur a pu changer depuis.
  function stripAudienceParams() {
    try {
      const url = new URL(location.href);
      if (!url.searchParams.has('audience') && !url.searchParams.has('mode')) return;
      url.searchParams.delete('audience');
      url.searchParams.delete('mode');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    } catch { }
  }

  const currentHeroMode = () => (rootEl.dataset.mode === 'cli' ? 'cli' : 'cand');

  /* =====================================================
     Synchros UI (features / apps / how)
  ===================================================== */
  function setFeat(m) {
    const isCli = (m === 'client' || m === 'cli');
    document.getElementById('features')?.setAttribute('data-action-mode', isCli ? 'cli' : 'cand');
    syncFeatSwitchUI(isCli ? 'cli' : 'cand');
    if (gridCli) {
      gridCli.hidden = !isCli;
      gridCli.classList.toggle('hidden', !isCli);
    }
    if (gridCand) {
      gridCand.hidden = isCli;
      gridCand.classList.toggle('hidden', isCli);
    }
    renderFeatureMode(isCli ? 'cli' : 'cand');
  }

  function getFeatureModeKey(mode) {
    return mode === 'cli' ? 'client' : 'candidate';
  }

  function getFeatureModeNodes(mode) {
    return mode === 'cli'
      ? { choices: featureChoicesCli, stage: featureStageCli }
      : { choices: featureChoicesCand, stage: featureStageCand };
  }

  function getFeatureItems(mode) {
    return I18N.t(`features.modes.${getFeatureModeKey(mode)}.items`, []);
  }

  function getFeatureChoiceAria(mode) {
    return I18N.t(`features.modes.${getFeatureModeKey(mode)}.choiceAria`, '');
  }

  function getFeatureMedia(mode, index) {
    const baseMedia = FEATURE_SHOWCASE_MEDIA[mode]?.[index] || FEATURE_SHOWCASE_MEDIA[mode]?.[0] || null;
    if (!baseMedia) return null;

    const lang = (window.I18N?.lang || getLang()).toLowerCase().startsWith('en') ? 'en' : 'fr';
    const asset = lang === 'en'
      ? (baseMedia.assetEn || baseMedia.assetFr)
      : baseMedia.assetFr;

    return asset ? { ...baseMedia, asset } : null;
  }

  function renderFeatureChoices(mode, items, activeIndex) {
    const aria = escapeHtml(getFeatureChoiceAria(mode));
    return `
      <div class="feature-choice-list" role="group" aria-label="${aria}">
        ${items.map((item, index) => {
      const active = index === activeIndex;
      const meta = item?.preview?.tag || item?.points?.[0] || '';
      return `
            <button
              type="button"
              class="feature-choice${active ? ' is-active' : ''}"
              data-feature-mode="${mode}"
              data-feature-index="${index}"
              aria-pressed="${active ? 'true' : 'false'}"
            >
              <span class="feature-choice__index">${String(index + 1).padStart(2, '0')}</span>
              <span class="feature-choice__copy">
                <span class="feature-choice__label">${escapeHtml(item?.label || '')}</span>
                ${meta ? `<span class="feature-choice__meta">${escapeHtml(meta)}</span>` : ''}
              </span>
            </button>
          `;
    }).join('')}
      </div>
    `;
  }

  function renderFeatureStage(mode, item, index) {
    const media = getFeatureMedia(mode, index);
    if (!item || !media) return '';

    const preview = item.preview || {};
    const points = Array.isArray(item.points) ? item.points : [];
    const support = item.support || {};
    const supportLines = Array.isArray(support.lines)
      ? support.lines
      : (Array.isArray(preview.rows) ? preview.rows.slice(0, 2) : []);
    const supportLabel = support.label || preview.tag || '';
    const supportTitle = support.title || preview.headline || '';

    return `
      <div class="feature-stage__panel feature-stage__panel--${escapeHtml(media.kind)}">
        <div class="feature-stage__visual">
          <div class="feature-product feature-product--${escapeHtml(media.shell)}">
            <div class="feature-product__shell feature-product__shell--${escapeHtml(media.kind)}">
              ${preview.badge ? `<span class="feature-product__badge">${escapeHtml(preview.badge)}</span>` : ''}

              <div class="feature-product__frame">
                <div class="feature-product__image feature-product__image--${escapeHtml(media.shell)}">
                  <img
                    src="${escapeHtml(encodeURI(media.asset))}"
                    loading="lazy"
                    decoding="async"
                    alt="${escapeHtml(item.alt || '')}"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="feature-details">
          <h3>${escapeHtml(item.title || '')}</h3>
          <p>${escapeHtml(item.desc || '')}</p>
          ${points.length ? `
            <ul class="feature-points">
              ${points.map((point) => `<li>${escapeHtml(point)}</li>`).join('')}
            </ul>
          ` : ''}
          ${(preview.badge || preview.cta) ? `
            <div class="feature-details__status">
              ${preview.badge ? `<span class="feature-details__status-kicker">${escapeHtml(preview.badge)}</span>` : ''}
              ${preview.cta ? `<span class="feature-details__status-copy">${escapeHtml(preview.cta)}</span>` : ''}
            </div>
          ` : ''}
          ${(supportLabel || supportTitle || supportLines.length) ? `
            <div class="feature-support">
              ${supportLabel ? `<span class="feature-support__label">${escapeHtml(supportLabel)}</span>` : ''}
              ${supportTitle ? `<strong class="feature-support__title">${escapeHtml(supportTitle)}</strong>` : ''}
              ${supportLines.length ? `
                <ul class="feature-support__lines">
                  ${supportLines.map((line) => `<li>${escapeHtml(line)}</li>`).join('')}
                </ul>
              ` : ''}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  function renderFeatureMode(mode) {
    const { choices, stage } = getFeatureModeNodes(mode);
    const items = getFeatureItems(mode);
    if (!choices || !stage || !Array.isArray(items) || !items.length) return;

    const maxIndex = Math.max(0, items.length - 1);
    const activeIndex = Math.min(featureShowcaseState[mode] ?? 0, maxIndex);
    featureShowcaseState[mode] = activeIndex;

    choices.innerHTML = renderFeatureChoices(mode, items, activeIndex);
    stage.innerHTML = renderFeatureStage(mode, items[activeIndex], activeIndex);

    stage.querySelectorAll('.feature-product__image img').forEach((img) => {
      img.addEventListener('error', () => {
        img.closest('.feature-product__image')?.classList.add('is-fallback');
        img.remove();
      }, { once: true });
    });
  }

  function renderFeatureShowcase() {
    renderFeatureMode('cand');
    renderFeatureMode('cli');
  }

  function bindFeatureShowcase() {
    const section = document.getElementById('features');
    if (!section || section.dataset.featureShowcaseBound === 'true') return;
    section.dataset.featureShowcaseBound = 'true';

    section.addEventListener('click', (event) => {
      const button = event.target.closest('.feature-choice');
      if (!button) return;
      const mode = button.getAttribute('data-feature-mode') === 'cli' ? 'cli' : 'cand';
      const index = Number(button.getAttribute('data-feature-index') || '0');
      featureShowcaseState[mode] = Number.isFinite(index) ? index : 0;
      renderFeatureMode(mode);
    });
  }

  function setDl(m) {
    const isCli = (m === 'client' || m === 'cli');
    appsLeftCli?.classList.toggle('hidden', !isCli);
    appsLeftCand?.classList.toggle('hidden', isCli);

    if (appsPhone) {
      const candImg = appsPhone.dataset.cand || 'assets/hero-phone.webp';
      const cliImg = appsPhone.dataset.cli || 'assets/apps-client.webp';
      const wanted = isCli ? cliImg : candImg;

      if (!sameAsset(appsPhone.src, wanted)) appsPhone.src = wanted;
      appsPhone.alt = isCli ? 'Aperçu — Portail client' : 'Aperçu — App candidat';
    }
    syncAppsSwitchUI(isCli ? 'cli' : 'cand');

    // Swap notification toast per mode
    const notifCand = document.getElementById('appsNotifCand');
    const notifCli  = document.getElementById('appsNotifCli');
    if (notifCand) notifCand.classList.toggle('hidden', isCli);
    if (notifCli)  notifCli.classList.toggle('hidden', !isCli);
  }

  function setHowMode(m) {
    const isCli = (m === 'client' || m === 'cli');
    const mode = isCli ? 'cli' : 'cand';
    syncHowSwitchUI(mode);
    setHowBlockState(howCopyCand, !isCli);
    setHowBlockState(howCopyCli, isCli);
    setHowBlockState(howBenefitsCand, !isCli);
    setHowBlockState(howBenefitsCli, isCli);
    setHowBlockState(howVisualCand, !isCli);
    setHowBlockState(howVisualCli, isCli);
    setHowBlockState(howFlowCand, !isCli);
    setHowBlockState(howFlowCli, isCli);
  }

  let heroInitialRender = true;
  function renderHero(mode) {
    // Textes : variantes [data-audience-only] du HTML, basculées par le CSS.
    // Image-only transition: slide out → swap → slide in
    if (heroPhone) {
      const wanted = (mode === 'cli') ? 'assets/client-hero.svg' : 'assets/cand-hero.svg';
      if (!heroInitialRender && heroSection && !sameAsset(heroPhone.src, wanted)) {
        heroSection.classList.add('hero--img-swap');
        setTimeout(() => {
          heroPhone.src = wanted;
          heroPhone.alt = (mode === 'cli') ? 'Aperçu Staffr — Client' : 'Aperçu Staffr — Candidat';
          void heroPhone.offsetHeight;
          heroSection.classList.remove('hero--img-swap');
        }, 180);
      } else {
        if (!sameAsset(heroPhone.src, wanted)) heroPhone.src = wanted;
        heroPhone.alt = (mode === 'cli') ? 'Aperçu Staffr — Client' : 'Aperçu Staffr — Candidat';
        heroInitialRender = false;
      }
    }
    updateLandingCtas(mode);
  }

  /* =====================================================
     FAQ — selectors + sync
     (Toggle drag/click/keyboard handled by global wireToggle;
      label + knob rotation synced here, same as other toggles)
  ===================================================== */
  const faqModeToggle = $('#faqModeToggle');
  const faqSwitchLabel = $('#faqSwitchLabel');
  const faqKnobInner = faqModeToggle?.querySelector('.knob-inner');
  const faqCandidate = $('#faqCandidate');
  const faqClient = $('#faqClient');
  let faqRot = 0;

  function setFaq(m) {
    const isCli = (m === 'client' || m === 'cli');
    // Sync label + knob (same pattern as syncFeatSwitchUI / syncAppsSwitchUI)
    if (faqSwitchLabel) faqSwitchLabel.textContent = labelFor(isCli ? 'cli' : 'cand');
    faqRot += isCli ? 360 : -360;
    if (faqKnobInner) faqKnobInner.style.setProperty('--rot', faqRot + 'deg');
    // Show/hide FAQ panels
    if (faqCandidate) faqCandidate.hidden = isCli;
    if (faqClient) faqClient.hidden = !isCli;
    // Collapse all open accordions on mode switch
    document.querySelectorAll('#faq .faq-q[aria-expanded="true"]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (panel) panel.classList.remove('is-open');
    });
  }

  /* =====================================================
     Fonction centrale — setHero(mode)
  ===================================================== */
  let rot = 0;
  // opts.persist (défaut true) : false pour les applications d'état qui ne sont
  // pas un choix du visiteur (chargement, resynchronisation).
  function setHero(m, opts = {}) {
    const mode = (m === 'cli' || m === 'client') ? 'cli' : 'cand';
    heroSwitch?.setAttribute('data-mode', mode);
    heroSwitch?.setAttribute('aria-checked', String(mode === 'cli'));
    if (switchLabel) switchLabel.textContent = labelFor(mode);

    renderHero(mode);
    setFeat(mode);
    setDl(mode);
    setHowMode(mode);
    setFaq(mode);

    rot += (mode === 'cli') ? 360 : -360;
    knobInner?.style.setProperty('--rot', rot + 'deg');

    // 🔔 Sync global pour tous les composants et toggles
    document.documentElement.dataset.mode = mode;
    document.dispatchEvent(new CustomEvent('modechange', { detail: { mode } }));

    if (opts.persist !== false) persistAudience(mode);
  }

  function persistAudience(mode) {
    try { localStorage.setItem('heroMode', (mode === 'cli' ? 'client' : 'candidat')); } catch { }
  }

  /* Bascule en milieu de page : la section en cours de lecture garde sa place.
     Les sections au-dessus n'ont pas la même hauteur d'une audience à l'autre ;
     sans recalage, le contenu lu « saute ». Le scroll anchoring natif ne suffit
     pas (absent de Safari, abandonné par Chrome quand son ancre est dans un bloc
     masqué par la bascule) : il est suspendu pendant l'opération. */
  function jumpBy(dy) {
    // <html> porte scroll-behavior: smooth : le recalage doit être instantané.
    try {
      window.scrollBy({ top: dy, left: 0, behavior: 'instant' });
      return;
    } catch { /* valeur 'instant' inconnue : repli ci-dessous */ }
    const prev = rootEl.style.scrollBehavior;
    rootEl.style.scrollBehavior = 'auto';
    void rootEl.offsetHeight; // applique le style avant de défiler
    window.scrollBy(0, dy);
    rootEl.style.scrollBehavior = prev;
  }

  function keepReadingPosition(update) {
    const line = parseInt(getComputedStyle(rootEl).getPropertyValue('--header-height'), 10) || 64;
    const anchor = window.scrollY > 8
      ? Array.from(document.querySelectorAll('#main-content > section'))
        .find((s) => s.getBoundingClientRect().bottom > line)
      : null;
    if (!anchor) { update(); return; }
    const before = anchor.getBoundingClientRect().top;
    rootEl.style.overflowAnchor = 'none';
    update();
    const delta = anchor.getBoundingClientRect().top - before;
    if (Math.abs(delta) > 1) jumpBy(delta);
    requestAnimationFrame(() => requestAnimationFrame(() => { rootEl.style.overflowAnchor = ''; }));
  }

  // ✅ Pipeline public pour les toggles (drag/click) et le sélecteur d'audience.
  //    Un choix du visiteur bascule sur place ; l'application d'état au
  //    chargement (persist: false) ne touche pas au scroll.
  //    Un choix explicite de l'audience déjà affichée (ex. arrivée par
  //    ?audience=client avec une préférence candidat) est mémorisé sans re-rendu.
  window.applyMode = (m, opts = {}) => {
    if (opts.persist === false) { setHero(m, opts); return; }
    const mode = (m === 'cli' || m === 'client') ? 'cli' : 'cand';
    if (mode === currentHeroMode()) { persistAudience(mode); return; }
    keepReadingPosition(() => setHero(mode, opts));
  };

  /* =====================================================
     Mission tabs
  ===================================================== */
  const missionToggle = $('#missionToggle');
  const tabHow = $('#tabHow');
  const tabMission = $('#tabMission');
  const tabAbout = $('#tabAbout');
  const panelHow = $('#panelHow');
  const panelMission = $('#panelMission');
  const panelAbout = $('#panelAbout');

  function setMission(tab) {
    if (!missionToggle) return;
    missionToggle.setAttribute('data-tab', tab);
    tabHow?.setAttribute('aria-selected', String(tab === 'how'));
    tabMission?.setAttribute('aria-selected', String(tab === 'mission'));
    tabAbout?.setAttribute('aria-selected', String(tab === 'about'));
    if (panelHow) { panelHow.hidden = (tab !== 'how'); panelHow.classList.toggle('hidden', tab !== 'how'); }
    if (panelMission) { panelMission.hidden = (tab !== 'mission'); panelMission.classList.toggle('hidden', tab !== 'mission'); }
    if (panelAbout) { panelAbout.hidden = (tab !== 'about'); panelAbout.classList.toggle('hidden', tab !== 'about'); }
    syncSegIndicator();
  }
  tabHow?.addEventListener('click', () => setMission('how'));
  tabMission?.addEventListener('click', () => setMission('mission'));
  tabAbout?.addEventListener('click', () => setMission('about'));

  /* Segmented control: measure active button → position indicator */
  function syncSegIndicator() {
    if (!missionToggle) return;
    const active = missionToggle.querySelector('[aria-selected="true"]');
    if (!active) return;
    const containerRect = missionToggle.getBoundingClientRect();
    const btnRect = active.getBoundingClientRect();
    const left = btnRect.left - containerRect.left;
    const width = btnRect.width;
    missionToggle.style.setProperty('--seg-left', left + 'px');
    missionToggle.style.setProperty('--seg-width', width + 'px');
  }

  let segSyncTimer = null;
  function scheduleSegIndicatorSync() {
    if (!missionToggle) return;
    if (segSyncTimer) window.clearTimeout(segSyncTimer);

    requestAnimationFrame(() => {
      requestAnimationFrame(syncSegIndicator);
    });

    segSyncTimer = window.setTimeout(syncSegIndicator, 120);
  }

  // Différé : le seg indicator est ajusté visuellement après le first paint,
  // pas critique pour le LCP. Réduit le TBT de ~10-30ms selon le device.
  document.addEventListener('DOMContentLoaded', () => window.whenIdle(scheduleSegIndicatorSync));
  window.addEventListener('load', scheduleSegIndicatorSync);
  window.addEventListener('resize', scheduleSegIndicatorSync, { passive: true });
  window.addEventListener('orientationchange', scheduleSegIndicatorSync);
  window.addEventListener('i18n:changed', scheduleSegIndicatorSync);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleSegIndicatorSync).catch(() => { });
  }

  if (typeof ResizeObserver !== 'undefined' && missionToggle) {
    const missionToggleObserver = new ResizeObserver(() => scheduleSegIndicatorSync());
    missionToggleObserver.observe(missionToggle);
    missionToggle.querySelectorAll('button').forEach(btn => missionToggleObserver.observe(btn));
  }

  /* =====================================================
     Header shadow
  ===================================================== */
  const siteHeader = $('.site-header');
  const onScroll = () => {
    if (!siteHeader) return;
    if (window.scrollY > 10) siteHeader.classList.add('scrolled');
    else siteHeader.classList.remove('scrolled');
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  /* =====================================================
     Footer year
  ===================================================== */
  const yearEl = $('#year');
  if (yearEl) yearEl.textContent = new Date().getFullYear();

  /* =====================================================
     Init
  ===================================================== */
  function initialBoot() {
    setMission('how');
    const initial = getInitialAudience();
    bindFeatureShowcase();
    // Un lien ?audience= (campagne) devient la préférence seulement si le visiteur
    // n'en a pas déjà une : un choix fait sur le site n'est jamais écrasé par un lien.
    setHero(initial.mode, { persist: initial.source === 'url' && !getSavedAudience() });
    if (initial.source === 'url') stripAudienceParams();
    rootEl.removeAttribute('data-audience-boot');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialBoot, { once: true });
  } else {
    initialBoot();
  }

  window.addEventListener('hashchange', () => {
    if (/^#\/admin(\/|$)/i.test(location.hash)) { location.reload(); return; }
    const m = (location.hash.match(/mode=(candidat|client)/i)?.[1] || '').toLowerCase();
    if (m) setHero(m === 'client' ? 'cli' : 'cand');
  });

  /* =====================================================
     FAQ accordion — open/close
     (Toggle click/swipe/keyboard handled by global wireToggle)
  ===================================================== */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.faq-q');
    if (!btn) return;
    const isOpen = btn.getAttribute('aria-expanded') === 'true';
    const panelId = btn.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;

    // Close all siblings in same set
    const set = btn.closest('[data-faq-set]');
    if (set) {
      set.querySelectorAll('.faq-q[aria-expanded="true"]').forEach(ob => {
        if (ob !== btn) {
          ob.setAttribute('aria-expanded', 'false');
          const op = document.getElementById(ob.getAttribute('aria-controls'));
          if (op) op.classList.remove('is-open');
        }
      });
    }

    // Toggle current
    btn.setAttribute('aria-expanded', String(!isOpen));
    if (panel) {
      if (isOpen) panel.classList.remove('is-open');
      else panel.classList.add('is-open');
    }
  });

})();

/* =====================================================
   TESTIMONIALS — bulles avec position MANUELLE
===================================================== */
(() => {
  const TESTIMONIAL_LAYOUTS = [
    { x: 20, y: 20, size: 130 },
    { x: 60, y: 28, size: 120 },
    { x: 24, y: 70, size: 110 },
    { x: 42, y: 52, size: 140 },
    { x: 80, y: 55, size: 130 },
    { x: 60, y: 75, size: 120 }
  ];
  const TESTIMONIAL_IMAGES = [
    "assets/testi-1.webp",
    "assets/testi-2.webp",
    "assets/testi-3.webp",
    "assets/testi-4.webp",
    "assets/testi-5.webp",
    "assets/testi-6.webp"
  ];
  const TESTIMONIAL_NAMES = {
    cand: ["Samuel P.", "Olivier D.", "Julien M.", "Amélie R.", "Émilie L.", "Mathieu B."],
    cli: ["Laurent T.", "Nicolas B.", "Christophe M.", "Pascale R.", "Isabelle G.", "Thomas L."]
  };
  const ROTATION_DELAY = 3000;

  const wrap = document.getElementById("testiBubbles");
  const quoteEl = document.getElementById("testiQuote");
  const nameEl = document.getElementById("testiName");
  const roleEl = document.getElementById("testiRole");
  if (!wrap || !quoteEl || !nameEl || !roleEl) return;

  const isEN = () => (document.documentElement.lang || "fr").toLowerCase().startsWith("en");
  const currentMode = () => (document.documentElement.dataset.mode === "cli" ? "cli" : "cand");
  let activeIndex = 0;
  let rotationTimer = null;
  let resumeTimer = null;

  function currentTestimonials() {
    const mode = currentMode();
    return TESTIMONIAL_NAMES[mode].map((name, index) => ({
      id: index,
      mode,
      name,
      img: TESTIMONIAL_IMAGES[index],
      layout: TESTIMONIAL_LAYOUTS[index]
    }));
  }

  function testiText(t, key) {
    if (window.I18N && typeof window.I18N.t === "function") {
      const val = I18N.t(`testi.${t.mode}.${t.id}.${key}`, null);
      if (val && val !== "null" && val !== "") return val;
    }
    return "";
  }

  function hydrateCard(t) {
    if (!t) return;
    const quote = testiText(t, "quote") || (isEN() ? "Great experience." : "Super expérience.");
    const role = testiText(t, "role") || (t.mode === "cli" ? "Client" : "Candidat");

    quoteEl.textContent = isEN() ? `“ ${quote} ”` : `« ${quote} »`;
    nameEl.textContent = t.name;
    roleEl.textContent = role;
  }

  wrap.setAttribute("role", "group");
  if (!wrap.getAttribute("aria-label")) {
    wrap.setAttribute("aria-label", "Avatars témoignages");
  }

  let bubbleNodes = [];

  function stopRotation() {
    if (rotationTimer) {
      window.clearInterval(rotationTimer);
      rotationTimer = null;
    }
  }

  function clearResumeTimer() {
    if (resumeTimer) {
      window.clearTimeout(resumeTimer);
      resumeTimer = null;
    }
  }

  function startRotation() {
    stopRotation();
    if (bubbleNodes.length < 2 || document.hidden) return;
    // Pas d'auto-rotation sur mobile (≤ 768px) — fait mal aux yeux
    if (typeof window !== 'undefined' && window.innerWidth <= 768) return;
    rotationTimer = window.setInterval(() => {
      setActiveIndex((activeIndex + 1) % bubbleNodes.length, { userInitiated: false });
    }, ROTATION_DELAY);
  }

  function restartRotationAfterInteraction() {
    stopRotation();
    clearResumeTimer();
    resumeTimer = window.setTimeout(() => {
      resumeTimer = null;
      startRotation();
    }, ROTATION_DELAY);
  }

  function applyLayout() {
    const testimonials = currentTestimonials();
    bubbleNodes.forEach((btn) => {
      const id = parseInt(btn.dataset.id, 10);
      const t = testimonials.find(x => x.id === id);
      if (!t || !t.layout) return;

      const { x, y, size } = t.layout;
      if (typeof size === "number") {
        btn.style.setProperty("--s", `${size}px`);
      }
      if (typeof x === "number") {
        btn.style.setProperty("--x", `${x}%`);
      }
      if (typeof y === "number") {
        btn.style.setProperty("--y", `${y}%`);
      }
    });
  }

  function renderBubbles() {
    stopRotation();
    clearResumeTimer();
    wrap.innerHTML = "";
    bubbleNodes = [];
    const testimonials = currentTestimonials();

    testimonials.forEach((t) => {
      const btn = document.createElement("button");
      btn.className = "bubble";
      btn.type = "button";
      btn.dataset.id = String(t.id);
      btn.style.backgroundImage = `url('${t.img}')`;
      btn.setAttribute("aria-label", t.name);
      btn.setAttribute("aria-pressed", "false");
      wrap.appendChild(btn);
      bubbleNodes.push(btn);
    });

    bubbleNodes.forEach((btn) => {
      btn.addEventListener("click", () => setActiveIndex(parseInt(btn.dataset.id, 10), { userInitiated: true }));
      btn.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setActiveIndex(parseInt(btn.dataset.id, 10), { userInitiated: true });
        }
      });
    });

    applyLayout();
    setActiveIndex(0, { userInitiated: false });
    startRotation();
  }

  function setActiveIndex(index, options = {}) {
    const testimonials = currentTestimonials();
    if (!testimonials.length || !bubbleNodes.length) return;
    activeIndex = ((index % testimonials.length) + testimonials.length) % testimonials.length;

    bubbleNodes.forEach((b, i) => {
      const isActive = i === activeIndex;
      b.classList.toggle("is-active", isActive);
      b.setAttribute("aria-pressed", String(isActive));
    });

    hydrateCard(testimonials[activeIndex]);

    if (options.userInitiated) {
      restartRotationAfterInteraction();
    }
  }

  renderBubbles();

  window.addEventListener("resize", () => {
    applyLayout();
  });

  const rehydrateText = () => {
    setActiveIndex(activeIndex, { userInitiated: false });
  };
  document.addEventListener("i18n:change", rehydrateText);
  window.addEventListener("i18n:changed", rehydrateText);
  window.addEventListener("load", rehydrateText);
  document.addEventListener("modechange", () => renderBubbles());
  document.addEventListener("visibilitychange", () => {
    clearResumeTimer();
    if (document.hidden) stopRotation();
    else startRotation();
  });
})();


/* =====================================================
   HERO — Slider mobile-only avec dots (no-op desktop)
===================================================== */
(() => {
  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
  const track = document.getElementById('modeCards');
  const dotsBox = document.getElementById('heroDots');
  if (!track || !dotsBox) return;

  dotsBox.setAttribute('role', 'group');
  dotsBox.setAttribute('aria-label', 'Navigation des cartes');

  let cards = [];
  let dots = [];
  let raf = null;
  let cardOffsets = [];
  const GAP = 12;

  function placeDotsUnderCards() {
    if (track.nextElementSibling !== dotsBox) {
      track.after(dotsBox);
    }
  }

  function measureCards() {
    cardOffsets = [];
    if (!cards.length) return;
    let acc = 0;
    cards.forEach((c, i) => {
      const w = c.offsetWidth || 0;
      cardOffsets[i] = acc;
      acc += Math.round(w) + GAP;
    });
  }

  function setActive(i) {
    dots.forEach((d, idx) => {
      const isActive = idx === i;
      d.classList.toggle('is-active', isActive);
      d.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function nearestIndex() {
    const scrollLeft = track.scrollLeft;
    if (!cardOffsets.length) return 0;
    let bestIdx = 0;
    let bestDist = Infinity;
    cardOffsets.forEach((pos, i) => {
      const dist = Math.abs(scrollLeft - pos);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    });
    return bestIdx;
  }

  function onScroll() {
    if (!isMobile()) return;
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => setActive(nearestIndex()));
  }

  function rebuildDots() {
    placeDotsUnderCards();
    dotsBox.innerHTML = '';
    cards = Array.from(track.querySelectorAll(':scope > .card'));

    cards.forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hmc-dot';
      b.setAttribute('aria-label', `Aller à la carte ${i + 1}`);
      b.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');

      b.addEventListener('click', () => {
        if (!isMobile()) return;
        if (!cardOffsets.length) {
          requestAnimationFrame(() => {
            measureCards();
            const targetScroll = cardOffsets[i] ?? 0;
            track.scrollTo({ left: targetScroll, behavior: 'smooth' });
            setActive(i);
          });
        } else {
          const targetScroll = cardOffsets[i] ?? 0;
          track.scrollTo({ left: targetScroll, behavior: 'smooth' });
          setActive(i);
        }
      });
      dotsBox.appendChild(b);
    });

    dots = Array.from(dotsBox.querySelectorAll('.hmc-dot'));

    requestAnimationFrame(() => {
      measureCards();
      setActive(0);
    });
  }

  function enable() {
    if (!isMobile()) return;
    rebuildDots();
    track.removeEventListener('scroll', onScroll);
    track.addEventListener('scroll', onScroll, { passive: true });
  }

  function disable() {
    track.removeEventListener('scroll', onScroll);
    dotsBox.innerHTML = '';
    dots = [];
    cardOffsets = [];
  }

  let wasMobile = isMobile();
  if (wasMobile) enable();

  window.addEventListener('resize', () => {
    const now = isMobile();
    if (now && !wasMobile) { enable(); }
    else if (!now && wasMobile) { disable(); }
    wasMobile = now;
  });

  window.addEventListener('heroCardsUpdated', () => {
    if (!isMobile()) return;
    rebuildDots();
  });
})();

/* =====================================================
   INDUSTRIES — bulles avec position MANUELLE
===================================================== */
(() => {
  const INDUSTRIES = [
    {
      id: 0,
      key: 'cuisine',
      name: 'Cuisine',
      img: 'assets/Kitchen-XP.webp',
      desc: 'Brigades rapides et expérimentées.',
      positions: ['Chef(fe)', 'Sous-chef(fe)', 'Cuisinier(ère)', 'Plongeur(se)', 'Préposé(e) à la préparation'],
      layout: { x: 16, y: 26, size: 130 }
    },
    {
      id: 1,
      key: 'service',
      name: 'Service',
      img: 'assets/Service-XP.webp',
      desc: 'Profils orientés client pour restaurants et banquets.',
      positions: ['Serveur(se)', 'Barman/Barmaid', 'Hôte(sse)', 'Runner', 'Chef(fe) d’équipe'],
      layout: { x: 22, y: 70, size: 110 }
    },
    {
      id: 2,
      key: 'hotel',
      name: 'Hôtellerie',
      img: 'assets/hotel-XP.webp',
      desc: 'Renfort front-desk et back-of-house.',
      positions: ['Réceptionniste', 'Concierge', 'Valet/Chasseur', 'Superviseur(se) de nuit'],
      layout: { x: 70, y: 58, size: 115 }
    },
    {
      id: 3,
      key: 'event',
      name: 'Événementiel',
      img: 'assets/Event.webp',
      desc: 'Équipes flexibles pour montages et service.',
      positions: ['Préposé(e) banquet', 'Chef(fe) d’équipe', 'Montage / démontage', 'Coordination terrain'],
      layout: { x: 46, y: 80, size: 125 }
    },
    {
      id: 4,
      key: 'housekeeping',
      name: 'Entretien ménager',
      img: 'assets/Cleaning.webp',
      desc: 'Standards hôteliers et procédures rigoureuses.',
      positions: ['Préposé(e) aux chambres', 'Aires publiques', 'Superviseur(se) entretien'],
      layout: { x: 38, y: 44, size: 135 }
    },
    {
      id: 5,
      key: 'manual',
      name: 'Manutention',
      img: 'assets/Genral-labor-XP.webp',
      desc: 'Renfort polyvalent entrepôts et logistique.',
      positions: ['Journalier(ère)', 'Aide générale', 'Aide-livraison'],
      layout: { x: 56, y: 24, size: 120 }
    }
  ];

  const wrap = document.getElementById('indBubbles');
  const nameEl = document.getElementById('indName');
  const descEl = document.getElementById('indDesc');
  const listEl = document.getElementById('indPositions');
  if (!wrap || !nameEl || !descEl || !listEl) return;

  wrap.setAttribute('role', 'group');
  if (!wrap.getAttribute('aria-label')) {
    wrap.setAttribute('aria-label', 'Industries couvertes');
  }

  const mmInd = window.matchMedia('(max-width: 768px)');
  let nodes = [];
  let activeIndex = 0;
  let rotationTimer = null;
  let resumeTimer = null;
  let setActiveIndustry = () => {};
  const ROTATION_DELAY = 5000;

  function stopRotation() {
    if (rotationTimer) {
      window.clearInterval(rotationTimer);
      rotationTimer = null;
    }
  }

  function clearResumeTimer() {
    if (resumeTimer) {
      window.clearTimeout(resumeTimer);
      resumeTimer = null;
    }
  }

  function startRotation() {
    stopRotation();
    if (nodes.length < 2 || document.hidden) return;
    // Pas d'auto-rotation sur mobile (≤ 768px) — fait mal aux yeux
    if (typeof window !== 'undefined' && window.innerWidth <= 768) return;
    rotationTimer = window.setInterval(() => {
      const nextIndex = (activeIndex + 1) % nodes.length;
      setActiveIndustry(nodes[nextIndex], { userInitiated: false });
    }, ROTATION_DELAY);
  }

  function restartRotationAfterInteraction() {
    stopRotation();
    clearResumeTimer();
    resumeTimer = window.setTimeout(() => {
      resumeTimer = null;
      startRotation();
    }, ROTATION_DELAY);
  }

  function hydrateCard(item) {
    if (!item) return;
    const t = (k, fb) => (window.I18N?.t(k, null) ?? fb);
    const base = `industries.items.${item.key}`;
    const name = t(`${base}.title`, item.name);
    const desc = t(`${base}.desc`, item.desc);

    nameEl.textContent = name;
    descEl.textContent = desc;
    listEl.innerHTML = '';
    const positions = (window.I18N?.t(`${base}.positions`, null)) || item.positions;
    (positions || []).forEach(p => {
      const li = document.createElement('li');
      li.textContent = p;
      listEl.appendChild(li);
    });

    // Plus de bouton "Voir plus" : toutes les positions sont visibles
    // et la card a une hauteur stable via min-height en CSS.
    const card = document.getElementById('indCard');
    if (card) {
      // Nettoie d'éventuels états anciens (compat ascendante)
      card.classList.remove('ind-card--has-more', 'ind-card--expanded');
      const existing = card.querySelector('.ind-card__more-btn');
      if (existing) existing.remove();
    }
  }

  function applyLayout() {
    nodes.forEach(btn => {
      const id = +btn.dataset.id;
      const it = INDUSTRIES.find(x => x.id === id);
      if (!it || !it.layout) return;

      const { x, y, size } = it.layout;
      /* Desktop affine a 0.88 (~12% plus petit) pour resserrer le visuel.
         Mobile inchange (0.7). */
      const factor = mmInd.matches ? 0.7 : 0.88;
      const s = size * factor;

      if (typeof x === 'number') btn.style.setProperty('--x', x + '%');
      if (typeof y === 'number') btn.style.setProperty('--y', y + '%');
      if (typeof s === 'number') btn.style.setProperty('--s', s + 'px');
    });
  }

  function render() {
    wrap.innerHTML = '';
    nodes = [];

    INDUSTRIES.forEach(it => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bubble';
      b.dataset.id = it.id;
      b.setAttribute('aria-label', it.name);
      b.setAttribute('aria-pressed', 'false');
      b.style.backgroundImage = `url('${it.img}')`;
      b.style.setProperty('--i', String(it.id));
      b.addEventListener('click', () => setActive(b, { userInitiated: true }));
      b.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(b, { userInitiated: true }); }
      });

      wrap.appendChild(b);
      nodes.push(b);
    });

    applyLayout();

    function setActive(el, options = {}) {
      if (!el) return;
      activeIndex = Math.max(0, nodes.indexOf(el));
      nodes.forEach(n => {
        n.classList.remove('is-active');
        n.setAttribute('aria-pressed', 'false');
      });
      el.classList.add('is-active');
      el.setAttribute('aria-pressed', 'true');
      const it = INDUSTRIES.find(x => x.id === +el.dataset.id);

      // Smooth content fade on card
      const card = document.getElementById('indCard');
      if (card) {
        card.classList.remove('ind-card--fade');
        void card.offsetHeight;
        card.classList.add('ind-card--fade');
      }

      hydrateCard(it);

      if (options.userInitiated) {
        restartRotationAfterInteraction();
      }
    }

    setActiveIndustry = setActive;
    if (nodes[0]) setActive(nodes[0]);
    startRotation();
  }

  render();

  const rehydrateCurrent = () => {
    const active = document.querySelector('#indBubbles .bubble.is-active');
    const id = active ? +active.dataset.id : 0;
    const it = INDUSTRIES.find(x => x.id === id) || INDUSTRIES[0];
    hydrateCard(it);
  };
  document.addEventListener('i18n:change', rehydrateCurrent);
  window.addEventListener('i18n:changed', rehydrateCurrent);
  document.addEventListener('visibilitychange', () => {
    clearResumeTimer();
    if (document.hidden) stopRotation();
    else startRotation();
  });

  mmInd.addEventListener('change', applyLayout);
})();

/* ==============================
   STATS — CountUp fluide + pulse
============================== */
(() => {
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const DURATION = 1400;

  function formatNumber(n, decimals = 0) {
    const f = new Intl.NumberFormat(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
    return f.format(n);
  }

  function animateValue(el) {
    const target = parseFloat(el.dataset.target || '0');
    const suffix = el.dataset.suffix || '';
    const decimals = parseInt(el.dataset.decimals || '0', 10);

    const start = performance.now();
    function frame(now) {
      const t = Math.min(1, (now - start) / DURATION);
      const v = target * easeOut(t);
      el.textContent = formatNumber(v, decimals) + suffix;

      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = formatNumber(target, decimals) + suffix;
        el.closest('.stat-card')?.classList.add('done');
      }
    }
    requestAnimationFrame(frame);
  }

  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const stat = entry.target;
        stat.classList.add('active');
        const valueEl = stat.querySelector('.stat-value');
        if (valueEl && !stat.dataset.started) {
          stat.dataset.started = '1';
          animateValue(valueEl);
        }
        io.unobserve(stat);
      }
    });
  }, { threshold: 0.28 });

  document.querySelectorAll('#stats .stat-card').forEach(s => io.observe(s));

  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq.matches) {
    document.querySelectorAll('#stats .stat-card').forEach(stat => {
      const el = stat.querySelector('.stat-value');
      if (!el) return;
      const target = parseFloat(el.dataset.target || '0');
      const suffix = el.dataset.suffix || '';
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      el.textContent = new Intl.NumberFormat(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals }).format(target) + suffix;
      stat.classList.add('active', 'done');
    });
  }
})();

/* =====================================================
   Animation FEATURES quand la section entre en vue
  ===================================================== */
(function initFeaturesAnimation() {
  const section = document.querySelector('#features');
  if (!section || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        section.classList.add('features-in-view');
        obs.unobserve(section);
      }
    });
  }, {
    root: null,
    threshold: 0.2
  });

  observer.observe(section);
})();

/* =========================================================
   ✅ Toggles unifiés (drag + click + clavier) -> setHero()
========================================================= */
(() => {
  const DOC = document;
  const clamp01 = v => Math.max(0, Math.min(1, v));

  function wireToggle(trackEl) {
    if (!trackEl) return;
    if (trackEl.dataset.modeToggleBound === 'true') return;
    trackEl.dataset.modeToggleBound = 'true';

    if (!trackEl.dataset.mode) trackEl.dataset.mode = 'cand';
    trackEl.setAttribute('role', 'switch');
    trackEl.setAttribute('tabindex', '0');
    trackEl.setAttribute('aria-checked', String(trackEl.dataset.mode === 'cli'));

    const apply = (next) => {
      (window.applyMode || window.setHero || function () { })(next);
    };

    /* CLICK */
    trackEl.addEventListener('click', () => {
      const next = trackEl.dataset.mode === 'cli' ? 'cand' : 'cli';
      apply(next);
    });

    /* CLAVIER */
    trackEl.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        const next = trackEl.dataset.mode === 'cli' ? 'cand' : 'cli';
        apply(next);
      }
      if (e.key === 'ArrowLeft') { apply('cand'); }
      if (e.key === 'ArrowRight') { apply('cli'); }
    });

    /* DRAG */
    let dragging = false;
    let rect = null;
    let pad = 2;
    let knob = 0;
    let usable = 0;
    let startPos = 0;
    let currentP = 0;
    let draggedFar = false;
    let activeId = null;

    const measure = () => {
      rect = trackEl.getBoundingClientRect();
      const cs = getComputedStyle(trackEl);
      pad = parseFloat(cs.getPropertyValue('--switch-pad')) || 2;
      knob = Math.max(0, rect.height - (pad * 2));
      usable = Math.max(1, rect.width - knob - (pad * 2));
    };

    const pFromX = x => {
      const v = x - rect.left - pad - (knob / 2);
      return clamp01(v / usable);
    };

    function onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      activeId = e.pointerId ?? null;
      measure();

      dragging = true;
      draggedFar = false;
      startPos = trackEl.dataset.mode === 'cli' ? 1 : 0;
      currentP = startPos;

      trackEl.classList.add('dragging');
      trackEl.style.setProperty('--drag', String(currentP));

      try { trackEl.setPointerCapture(activeId); } catch { }
    }

    function onMove(e) {
      if (!dragging) return;
      if (activeId != null && e.pointerId && e.pointerId !== activeId) return;
      if (!rect) return;

      currentP = pFromX(e.clientX);
      trackEl.style.setProperty('--drag', String(currentP));

      if (!draggedFar && Math.abs(currentP - startPos) > 0.25) {
        draggedFar = true;
      }
    }

    function onUp(e) {
      if (!dragging) return;
      dragging = false;

      if (draggedFar) {
        const next = currentP >= 0.5 ? 'cli' : 'cand';
        apply(next);

        const kill = ev => {
          ev.stopPropagation();
          ev.preventDefault();
          trackEl.removeEventListener('click', kill, true);
        };
        trackEl.addEventListener('click', kill, true);
      }

      try { trackEl.releasePointerCapture(activeId); } catch { }
      activeId = null;

      trackEl.classList.remove('dragging');
      trackEl.style.removeProperty('--drag');
    }

    trackEl.addEventListener('pointerdown', onDown);
    DOC.addEventListener('pointermove', onMove, { passive: true });
    DOC.addEventListener('pointerup', onUp);
    DOC.addEventListener('pointercancel', onUp);
  }

  window.__wireModeToggle = wireToggle;

  document
    .querySelectorAll('.hero-switch, .mini-switch')
    .forEach(wireToggle);

  document.addEventListener('modechange', (ev) => {
    const m = ev?.detail?.mode === 'cli' ? 'cli' : 'cand';
    document.querySelectorAll('.hero-switch, .mini-switch').forEach(sw => {
      sw.dataset.mode = m;
      sw.setAttribute('aria-checked', String(m === 'cli'));
      sw.classList.remove('dragging');
      sw.style.removeProperty('--drag');
    });

    document
      .querySelectorAll('[data-bind-mode], #features, #apps, #featuresGrid, .features-grid')
      .forEach(el => { el.dataset.mode = m; });
  });

  const initial = document.documentElement.dataset.mode || 'cand';
  (window.applyMode || window.setHero || function () { })(initial, { persist: false });
})();

/* =========================================================
   AUDIENCE — « Je suis : Entreprise | Candidat »
   • Sélecteurs [data-audience-set] : Hero + barre compacte persistante
   • Barre compacte (mobile et desktop, un seul élément) : visible dès que le
     sélecteur du Hero sort de l'écran, masquée quand il y revient
   • Écran d'entrée (mobile, 1re visite) : html[data-audience-entry], posé par le <head>
   Aucun état propre : tout passe par window.applyMode → setHero(), et le
   visuel suit html[data-mode] en CSS.
========================================================= */
(() => {
  const root = document.documentElement;
  const heroSwitch = document.getElementById('audienceHeroSwitch');
  const bar = document.getElementById('audienceBar');
  const entry = document.getElementById('audienceEntry');
  const status = document.getElementById('audienceStatus');
  if (!heroSwitch && !bar && !entry) return;

  const mqMobile = window.matchMedia('(max-width: 768px)');
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const applyMode = (mode) => (window.applyMode || function () { })(mode);
  const currentMode = () => (root.dataset.mode === 'cli' ? 'cli' : 'cand');
  const t = (key) => (window.I18N && typeof window.I18N.t === 'function' ? window.I18N.t(key, '') : '');
  const headerOffset = () => parseInt(getComputedStyle(root).getPropertyValue('--header-height'), 10) || 64;

  function syncPressed() {
    const mode = currentMode();
    document.querySelectorAll('[data-audience-set]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.audienceSet === mode));
    });
  }
  document.addEventListener('modechange', syncPressed);
  syncPressed();

  function announce(mode) {
    if (status) status.textContent = t(`audience.status.${mode}`);
  }

  // applyMode bascule sur place (position de lecture conservée, cf. keepReadingPosition).
  // Un clic sur l'audience déjà affichée reste un choix explicite : applyMode le
  // mémorise sans re-rendu.
  function switchAudience(mode) {
    const changed = mode !== currentMode();
    applyMode(mode);
    if (changed) announce(mode);
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-audience-set]');
    if (btn) switchAudience(btn.dataset.audienceSet === 'cli' ? 'cli' : 'cand');
  });

  /* ---- Barre compacte (mobile + desktop) ------------------------------ */
  if (bar && heroSwitch) {
    if ('IntersectionObserver' in window) {
      let io = null;
      const observe = () => {
        if (io) io.disconnect();
        io = new IntersectionObserver(([rec]) => {
          const scrolledPast = !rec.isIntersecting && rec.boundingClientRect.top < headerOffset();
          root.classList.toggle('audience-bar-visible', scrolledPast);
        }, { rootMargin: `-${headerOffset()}px 0px 0px 0px` });
        io.observe(heroSwitch);
      };
      observe();
      // --header-height change entre mobile et desktop : on recalcule la marge.
      if (mqMobile.addEventListener) mqMobile.addEventListener('change', observe);
    } else {
      root.classList.add('audience-bar-visible');
    }

    // Bas réel de la barre (en-tête + barre, selon l'écran et la langue) : sert aux
    // ancres (CSS scroll-margin-top, défilement mobile de script.js) pour que le titre
    // visé ne passe jamais sous la barre.
    const measureStack = () => {
      const bottom = parseFloat(getComputedStyle(bar).top) + bar.offsetHeight;
      if (bottom > 0) root.style.setProperty('--audience-stack', Math.round(bottom) + 'px');
    };
    measureStack();
    if ('ResizeObserver' in window) new ResizeObserver(measureStack).observe(bar);
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', measureStack);
  }

  /* ---- Écran d'entrée (mobile, 1re visite) ---------------------------- */
  if (!entry || !root.hasAttribute('data-audience-entry')) return;

  let isolated = [];
  let leaving = false;

  function close() {
    root.removeAttribute('data-audience-entry');
    isolated.forEach((el) => { el.inert = false; });
    isolated = [];
    entry.removeEventListener('click', onClick);
    document.removeEventListener('keydown', onKey);
    if (mqMobile.removeEventListener) mqMobile.removeEventListener('change', onViewport);
  }

  // Le focus rejoint le sélecteur du Hero : le visiteur voit (et le lecteur
  // d'écran annonce) où changer d'expérience plus tard.
  function focusHeroSwitch() {
    const active = heroSwitch && heroSwitch.querySelector('[aria-pressed="true"]');
    if (active) active.focus({ preventScroll: true });
  }

  // Fermeture sans choix (Échap, passage en largeur desktop) : rien n'est
  // mémorisé, l'écran reviendra à la prochaine visite.
  function dismiss() {
    if (leaving) return;
    close();
    focusHeroSwitch();
  }

  function choose(mode, card) {
    if (leaving) return;
    leaving = true;
    if (card) card.classList.add('is-chosen');
    applyMode(mode); // la page change sous l'écran, qui la dévoile ensuite
    const quick = mqReduce.matches;
    setTimeout(() => {
      root.setAttribute('data-audience-entry', 'leaving');
      setTimeout(() => {
        close();
        announce(mode);
        focusHeroSwitch();
        if (heroSwitch && !quick) {
          heroSwitch.classList.add('is-confirmed');
          setTimeout(() => heroSwitch.classList.remove('is-confirmed'), 900);
        }
      }, quick ? 0 : 220);
    }, quick ? 0 : 90);
  }

  function onClick(e) {
    const card = e.target.closest('[data-audience-choice]');
    if (card) {
      e.preventDefault();
      choose(card.dataset.audienceChoice === 'cli' ? 'cli' : 'cand', card);
      return;
    }
    if (e.target.closest('[data-audience-lang]') && typeof window.setLang === 'function') {
      window.setLang(window.I18N && window.I18N.lang === 'en' ? 'fr' : 'en')
        .catch((err) => console.error('Language switch failed:', err));
    }
  }

  // Dialogue modal : Tab et Maj+Tab bouclent sur ses contrôles (la page dessous
  // est inerte, mais sans boucle le focus partirait dans le chrome du navigateur).
  function onKey(e) {
    if (e.key === 'Escape') { dismiss(); return; }
    if (e.key !== 'Tab') return;
    const items = Array.from(entry.querySelectorAll('a[href], button:not([disabled])'))
      .filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const inside = entry.contains(active) && active !== entry;
    if (e.shiftKey && (!inside || active === first)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (!inside || active === last)) {
      e.preventDefault();
      first.focus();
    }
  }

  function onViewport() {
    if (!mqMobile.matches) dismiss();
  }

  if (!mqMobile.matches) {
    close();
    return;
  }
  // La page sous l'écran devient inerte (clavier + lecteurs d'écran).
  isolated = Array.from(document.body.children)
    .filter((el) => el !== entry && el.tagName !== 'SCRIPT' && !el.inert);
  isolated.forEach((el) => { el.inert = true; });
  entry.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey);
  if (mqMobile.addEventListener) mqMobile.addEventListener('change', onViewport);
  entry.focus({ preventScroll: true });
})();

/* ============================
   Cookie Consent (Fusion) — init différée
============================ */
let __cookieConsentInitDone = false;

function initCookieConsent() {
  // Si déjà initialisé, on ne refait rien
  if (__cookieConsentInitDone) return;

  const STORAGE_KEY = "cookie-consent";

  const banner = document.getElementById("cookie-banner");
  const modal = document.getElementById("cookie-modal");

  // Si le footer/HTML n'est pas encore là, on retentera plus tard
  if (!banner || !modal) return;

  banner.setAttribute('role', 'dialog');
  if (!banner.hasAttribute('aria-label') && !banner.hasAttribute('aria-labelledby')) {
    banner.setAttribute('aria-label', 'Gestion des cookies');
  }

  const acceptBtn = document.getElementById("cookie-accept");
  const rejectBtn = document.getElementById("cookie-reject");
  const customBtn = document.getElementById("cookie-customize");

  const saveBtn = document.getElementById("cookie-save");
  const closeBtn = document.getElementById("cookie-close");
  const modalRejectBtn = document.getElementById("cookie-modal-reject");

  const statsBox = document.getElementById("cookie-stats");
  const marketingBox = document.getElementById("cookie-marketing");

  if (!acceptBtn || !rejectBtn || !customBtn || !saveBtn || !closeBtn || !modalRejectBtn || !statsBox || !marketingBox) {
    console.warn('[cookie] Structure incomplète, bannière non initialisée.');
    return;
  }

  __cookieConsentInitDone = true;

  function getConsent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function hideBanner() {
    banner.classList.add("cookie-banner--hidden");
    banner.classList.remove("cookie-banner--visible");
  }

  function showBanner() {
    banner.classList.remove("cookie-banner--hidden");
    banner.classList.add("cookie-banner--visible");
  }

  function showModal() {
    modal.classList.remove("cookie-modal--hidden");
    modal.classList.add("is-open");
    modal.removeAttribute("hidden");
    document.documentElement.classList.add("cookies-open");
  }

  function hideModal() {
    modal.classList.add("cookie-modal--hidden");
    modal.classList.remove("is-open");
    modal.setAttribute("hidden", "hidden");
    document.documentElement.classList.remove("cookies-open");
  }

  function saveConsent(settings) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch { }
    hideBanner();
    hideModal();
    // Notifie le module analytics (Consent Mode v2) sans le coupler ici.
    document.dispatchEvent(new CustomEvent('consent:change', { detail: settings }));
  }

  function applyExistingConsent() {
    const consent = getConsent();
    if (!consent) {
      showBanner();
      return;
    }
    statsBox.checked = !!consent.stats;
    marketingBox.checked = !!consent.marketing;
  }

  // Boutons de la bannière
  acceptBtn.onclick = () => {
    saveConsent({
      necessary: true,
      stats: true,
      marketing: true,
      source: 'banner_accept_all'
    });
  };

  rejectBtn.onclick = () => {
    saveConsent({
      necessary: true,
      stats: false,
      marketing: false,
      source: 'banner_reject_all'
    });
  };

  customBtn.onclick = () => {
    showModal();
  };

  // Boutons de la modale
  saveBtn.onclick = () => {
    saveConsent({
      necessary: true,
      stats: statsBox.checked,
      marketing: marketingBox.checked,
      source: 'modal_save'
    });
  };

  modalRejectBtn.onclick = () => {
    saveConsent({
      necessary: true,
      stats: false,
      marketing: false,
      source: 'modal_reject_all'
    });
    statsBox.checked = false;
    marketingBox.checked = false;
  };

  closeBtn.onclick = hideModal;

  modal.querySelector(".cookie-modal__backdrop")?.addEventListener("click", hideModal);

  applyExistingConsent();
}

// Fallback : on essaie aussi à DOMContentLoaded (au cas où le footer serait inline).
// Défèré via whenIdle : le cookie banner est caché par défaut, il peut attendre
// après le first paint pour éviter de gonfler le TBT.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.whenIdle(initCookieConsent));
} else {
  window.whenIdle(initCookieConsent);
}

// ===============================
//  Cookies : ouverture des réglages (footer + délégation)
// ===============================
(function () {
  function getCookieModal() {
    // Supporte data-cookie-modal OU id="cookie-modal"
    return document.querySelector('[data-cookie-modal]') || document.getElementById('cookie-modal');
  }

  function openCookieModal() {
    initCookieConsent();

    var modal = getCookieModal();
    if (!modal) {
      console.warn('Cookie modal introuvable (data-cookie-modal ou #cookie-modal manquant).');
      return;
    }

    // Compat avec les deux systèmes : classe + hidden
    modal.classList.remove('cookie-modal--hidden');
    modal.classList.add('is-open');
    modal.removeAttribute('hidden');

    document.documentElement.classList.add('cookies-open');

    // Focus pour accessibilité
    var focusTarget =
      modal.querySelector('[data-cookie-modal-title]') ||
      modal.querySelector('h2, h1, [role="heading"]') ||
      modal;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  }

  function closeCookieModal() {
    var modal = getCookieModal();
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.classList.add('cookie-modal--hidden');
    modal.setAttribute('hidden', 'hidden');

    document.documentElement.classList.remove('cookies-open');
  }

  // 🟢 DÉLÉGATION : capte tous les clics, même sur des éléments injectés (footer/partials)
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-cookie-settings]');
    if (openBtn) {
      e.preventDefault();
      openCookieModal();
      return;
    }

    var closeBtn = e.target.closest('[data-cookie-close]');
    if (closeBtn) {
      e.preventDefault();
      closeCookieModal();
      return;
    }

    // Optionnel : clic sur l’overlay pour fermer (data-cookie-backdrop-close)
    var modal = getCookieModal();
    if (modal && modal.classList.contains('is-open')) {
      if (e.target === modal && modal.hasAttribute('data-cookie-backdrop-close')) {
        closeCookieModal();
      }
    }
  });

  // Escape pour fermer
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeCookieModal();
    }
  });
})();

/* =====================================================
   LOADER PARTIELS (header + footer)
===================================================== */
(function () {
  const bp = window.__BASE_PATH || '/';

  function resolveAssets(root) {
    if (!root) return;
    // data-src → src (images chargées dynamiquement)
    root.querySelectorAll('[data-src]').forEach(el => {
      el.src = bp + el.getAttribute('data-src');
    });
    // Réécrire les src relatifs (../assets/...)
    root.querySelectorAll('img[src^="../"]').forEach(el => {
      el.setAttribute('src', bp + el.getAttribute('src').replace(/^\.\.\//, ''));
    });
  }

  function resolveLinks(root) {
    if (!root) return;
    root.querySelectorAll('a[href]').forEach(el => {
      const h = el.getAttribute('href');
      if (!h || h.startsWith('#') || h.startsWith('http') || h.startsWith('mailto:') || h.startsWith('tel:') || h.startsWith('javascript:')) return;
      if (h.startsWith('../')) {
        // ../index.html#top → root index path resolved from the current base path
        el.setAttribute('href', bp + h.replace(/^\.\.\//, ''));
      } else if (!h.startsWith('/') && !h.includes('/')) {
        // Bare filename (ex: politique-de-confidentialite.html → /pages/...)
        el.setAttribute('href', bp + 'pages/' + h);
      }
    });
  }

  async function loadPartial(targetSelector, url) {
    const target = document.querySelector(targetSelector);
    if (!target) return;
    try {
      // Pas de `cache: 'no-cache'` : redondant avec le header serveur
      // Cache-Control: no-cache, no-store, must-revalidate déjà appliqué
      // sur les .html par .htaccess.
      const res = await fetch(bp + url);
      if (!res.ok) throw new Error(res.status);
      target.innerHTML = await res.text();
      resolveAssets(target);
      resolveLinks(target);
    } catch (err) {
      console.error('Erreur chargement:', url, err);
    }
  }

  // Idempotent bind for #langToggle
  function bindLangToggle() {
    const btn = document.querySelector('#langToggle');
    if (!btn || btn.dataset.bound === '1') return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const next = (window.I18N && window.I18N.lang === 'fr') ? 'en' : 'fr';
      if (typeof window.setLang === 'function') {
        window.setLang(next);
      } else if (window.I18N && typeof window.I18N.load === 'function') {
        window.I18N.load(next).then(() => {
          btn.textContent = (window.I18N.lang === 'fr') ? 'EN' : 'FR';
          document.querySelectorAll('.mnav__lang-btn').forEach(b => {
            b.setAttribute('aria-pressed', b.dataset.lang === window.I18N.lang ? 'true' : 'false');
          });
        });
      }
    });
  }

  // Re-apply i18n to newly injected partial elements
  function applyI18nToPartials() {
    if (!window.I18N || !window.I18N.dict || !Object.keys(window.I18N.dict).length) return;
    document.querySelectorAll('[data-i18n]').forEach(el => {
      const key = el.getAttribute('data-i18n');
      const val = window.I18N.t(key, null);
      setI18nValue(el, val);
    });
    document.querySelectorAll('[data-i18n-attr]').forEach(el => {
      const spec = el.getAttribute('data-i18n-attr');
      if (!spec) return;
      spec.split(';').forEach(pair => {
        const [attr, key] = pair.split(':').map(s => s?.trim()).filter(Boolean);
        if (!attr || !key) return;
        const val = window.I18N.t(key, null);
        if (val != null) el.setAttribute(attr, val);
      });
    });
    const langToggle = document.querySelector('#langToggle');
    if (langToggle && window.I18N) {
      langToggle.textContent = (window.I18N.lang === 'fr') ? 'EN' : 'FR';
    }
    document.querySelectorAll('.mnav__lang-btn').forEach(btn => {
      btn.setAttribute('aria-pressed', btn.dataset.lang === window.I18N.lang ? 'true' : 'false');
    });
  }

  document.addEventListener('DOMContentLoaded', async () => {
    await loadPartial('#site-header', 'partials/header.html');
    await loadPartial('#site-footer', 'partials/footer.html');

    // Post-injection: translate partials + bind langToggle
    applyI18nToPartials();
    bindLangToggle();

    if (window.afterHeaderFooterLoad) {
      window.afterHeaderFooterLoad();
    }
  });
})();

/* =====================================================
   HERO — Parallax doux (phone + glow)
===================================================== */
(() => {
  const hero = document.querySelector('.hero');
  const heroPhone = document.getElementById('heroPhone');
  if (!hero || !heroPhone) return;

  const docEl = document.documentElement;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function updateParallax() {
    if (prefersReduced.matches) {
      heroPhone.style.setProperty('--hero-parallax-phone', '0px');
      hero.style.setProperty('--hero-parallax-bg-x', '0px');
      hero.style.setProperty('--hero-parallax-bg-y', '0px');
      return;
    }

    const rect = hero.getBoundingClientRect();
    const vh = window.innerHeight || docEl.clientHeight;

    const center = rect.top + rect.height / 2;
    const distance = (center - vh / 2) / vh;
    const clamped = Math.max(-1, Math.min(1, distance));

    const phoneOffset = clamped * -18;
    const bgOffsetY = clamped * -30;
    const bgOffsetX = clamped * 8;

    heroPhone.style.setProperty('--hero-parallax-phone', phoneOffset.toFixed(1) + 'px');
    hero.style.setProperty('--hero-parallax-bg-y', bgOffsetY.toFixed(1) + 'px');
    hero.style.setProperty('--hero-parallax-bg-x', bgOffsetX.toFixed(1) + 'px');
  }

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateParallax();
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => updateParallax());
  window.addEventListener('load', () => updateParallax());
})();

/* =====================================================
   Scroll progress bar (haut de page)
===================================================== */
(() => {
  const bar = document.createElement('div');
  bar.id = 'scrollBar';
  document.body.appendChild(bar);

  function update() {
    const doc = document.documentElement;
    const scrollH = doc.scrollHeight - doc.clientHeight;
    const y = doc.scrollTop || window.pageYOffset || 0;
    const ratio = scrollH > 0 ? (y / scrollH) : 0;
    bar.style.transform = `scaleX(${ratio})`;
  }

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  window.addEventListener('load', update);
  update();
})();

/* =====================================================
   Nav — lien actif selon la section visible (robuste)
   (branché sur afterHeaderFooterLoad pour partials)
===================================================== */
(() => {
  function initNavActive() {
    if (!('IntersectionObserver' in window)) return;

    const header = document.querySelector('.site-header');
    if (!header) return;

    const links = Array.from(
      header.querySelectorAll('a[href^="#"]')
    ).filter(link => {
      const href = link.getAttribute('href') || '';
      if (!href.startsWith('#') || href.startsWith('#/')) return false;
      const id = href.slice(1);
      return !!document.getElementById(id);
    });

    if (!links.length) return;

    const items = links.map(link => {
      const id = (link.getAttribute('href') || '').slice(1);
      const sec = document.getElementById(id);
      return { id, link, sec };
    }).filter(x => !!x.sec);

    if (!items.length) return;

    const sections = items.map(x => x.sec);
    const lastSection = sections[sections.length - 1];

    function setActive(id) {
      items.forEach(({ id: sid, link }) => {
        const on = sid === id;
        link.classList.toggle('is-active', on);
      });
    }

    const observer = new IntersectionObserver((entries) => {
      let best = null;
      let bestOffset = Infinity;

      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        const rect = entry.target.getBoundingClientRect();
        const offset = Math.abs(rect.top);

        if (offset < bestOffset) {
          bestOffset = offset;
          best = entry.target.id;
        }
      });

      if (best) {
        setActive(best);
      }
    }, {
      root: null,
      threshold: 0,
      rootMargin: '-30% 0px -60% 0px'
    });

    sections.forEach(sec => observer.observe(sec));

    window.addEventListener('scroll', () => {
      const doc = document.documentElement;
      const scrollBottom = doc.scrollTop + window.innerHeight;
      const maxScroll = doc.scrollHeight;

      if (scrollBottom >= maxScroll - 5) {
        if (lastSection && lastSection.id) {
          setActive(lastSection.id);
        }
      }
    }, { passive: true });
  }

  if (document.readyState !== 'loading') {
    if (document.querySelector('.site-header')) {
      initNavActive();
    }
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.afterHeaderFooterLoad && document.querySelector('.site-header')) {
        initNavActive();
      }
    });
  }

  const prev = window.afterHeaderFooterLoad;
  window.afterHeaderFooterLoad = function () {
    if (typeof prev === 'function') prev();
    window.bindMobileNavToggle?.();
    window.bindMobileThemeToggle?.();
    // syncThemeIcon vit dans une autre IIFE ; on l'utilise via window (exposé en haut du fichier).
    window.syncThemeIcon?.();
    initNavActive();
    initCookieConsent();   // 🔥 init cookies après chargement header/footer
  };
})();

/* =====================================================
   SLIDERS MOBILE — FEATURES + POURQUOI STAFF RENTER
===================================================== */
(() => {
  const mqSlider = window.matchMedia('(max-width: 640px)');

  /**
   * Initialise un slider horizontal simple avec des dots.
   * @param {HTMLElement} trackEl   conteneur des cartes
   * @param {HTMLElement} dotsEl    conteneur des points (.slider-dots)
   */
  function initSimpleSlider(trackEl, dotsEl) {
    if (!trackEl || !dotsEl) return;
    const slides = Array.from(trackEl.children)
      .filter(el => el.nodeType === 1);

    if (slides.length <= 1) {
      dotsEl.innerHTML = '';
      dotsEl.style.display = 'none';
      return;
    }

    dotsEl.style.display = '';
    dotsEl.innerHTML = '';

    const dots = slides.map((_, idx) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'slider-dot' + (idx === 0 ? ' is-active' : '');
      btn.setAttribute('aria-label', 'Aller à l’élément ' + (idx + 1));
      btn.addEventListener('click', () => {
        const target = slides[idx];
        const offset = target.offsetLeft - trackEl.offsetLeft;
        trackEl.scrollTo({
          left: offset,
          behavior: 'smooth'
        });
      });
      dotsEl.appendChild(btn);
      return btn;
    });

    const updateActiveDot = () => {
      const scrollLeft = trackEl.scrollLeft;
      const viewportCenter = scrollLeft + trackEl.clientWidth / 2;
      let activeIdx = 0;
      let minDist = Infinity;

      slides.forEach((slide, idx) => {
        const center = slide.offsetLeft - trackEl.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(center - viewportCenter);
        if (dist < minDist) {
          minDist = dist;
          activeIdx = idx;
        }
      });

      dots.forEach((d, idx) => {
        d.classList.toggle('is-active', idx === activeIdx);
      });
    };

    trackEl.addEventListener('scroll', () => {
      if (!mqSlider.matches) return;
      window.requestAnimationFrame(updateActiveDot);
    });

    window.addEventListener('resize', () => {
      if (!mqSlider.matches) return;
      updateActiveDot();
    });

    updateActiveDot();
  }

  function setupFeatureSlider() {
    const featSection = document.getElementById('features');
    if (!featSection) return;

    // The action section is no longer a mobile slider in the current design.
    if (featSection.hasAttribute('data-action-mode')) {
      const existingDots = document.getElementById('featDots');
      if (existingDots) {
        existingDots.innerHTML = '';
        existingDots.style.display = 'none';
      }
      return;
    }

    let dotsEl = document.getElementById('featDots');
    if (!dotsEl) {
      dotsEl = document.createElement('div');
      dotsEl.id = 'featDots';
      dotsEl.className = 'slider-dots';
      featSection.appendChild(dotsEl);
    }

    const gridCand = document.getElementById('gridCand');
    const gridCli = document.getElementById('gridCli');

    const activeFeatGrid =
      [gridCand, gridCli].find(el => el && !el.classList.contains('hidden')) ||
      gridCand || gridCli;

    if (activeFeatGrid) {
      initSimpleSlider(activeFeatGrid, dotsEl);
    }
  }

  function setupWhySlider() {
    const whySection = document.getElementById('why');
    if (!whySection) return;

    const whyGrid = whySection.querySelector('.stats-why-grid');
    if (!whyGrid) return;

    let dotsEl = document.getElementById('whyDots');
    if (!dotsEl) {
      dotsEl = document.createElement('div');
      dotsEl.id = 'whyDots';
      dotsEl.className = 'slider-dots';
      whySection.appendChild(dotsEl);
    }

    initSimpleSlider(whyGrid, dotsEl);
  }

  function setupSliders() {
    if (!mqSlider.matches) return;
    setupFeatureSlider();
    setupWhySlider();
  }

  function boot() {
    setupSliders();
  }

  // Sliders mobiles : pas critiques pour le first paint, on défère via whenIdle
  // pour éviter de bloquer le main thread pendant le rendu initial.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.whenIdle(boot), { once: true });
  } else {
    window.whenIdle(boot);
  }

  // Quand on bascule CAND / CLI, on réinitialise le slider FEATURES sur mobile
  document.addEventListener('modechange', () => {
    if (!mqSlider.matches) return;
    setupFeatureSlider();
  });

  mqSlider.addEventListener('change', e => {
    if (e.matches) setupSliders();
  });


  // ==========================================
  // REVEAL ANIMATIONS (WHY STAFF RENTER)
  // Différé via whenIdle pour ne pas bloquer le first paint (TBT).
  // L'IntersectionObserver setup + querySelectorAll sur ~50 éléments
  // coûtait plusieurs ms de long task.
  // ==========================================
  window.whenIdle(function initRevealAnimations() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: reveal immediately
      document.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target); // Only animate once
        }
      });
    }, {
      rootMargin: '0px 0px -100px 0px', // Trigger slightly before coming into view
      threshold: 0.1
    });

    document.querySelectorAll('[data-reveal]').forEach(el => {
      observer.observe(el);
    });
  });


  document.addEventListener('DOMContentLoaded', () => {
    const root = document.documentElement;
    const theme = root.getAttribute('data-theme') || 'light';
    const mnavTheme = document.getElementById('mnav-theme');

    if (mnavTheme) {
      mnavTheme.checked = (theme === 'dark');
    }
  });

  /* =====================================================
     STORE BADGES — QR popup on desktop
  ===================================================== */
  (function initStoreQrPopup() {
    const modal = document.getElementById('qr-modal');
    if (!modal) return;
    const qrImg = document.getElementById('qr-modal-img');
    const qrStore = document.getElementById('qr-modal-store');
    const qrLink = document.getElementById('qr-modal-link');
    const badges = document.querySelectorAll('.store-badges a[data-qr]');
    if (!badges.length) return;

    const isCoarsePointer = () =>
      window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;

    let lastFocus = null;

    function open(badge) {
      const qrSrc = badge.getAttribute('data-qr');
      const store = badge.getAttribute('data-store');
      const url = badge.getAttribute('href');
      if (!qrSrc) return;
      qrImg.src = qrSrc;
      qrImg.alt = 'QR ' + (store === 'ios' ? 'App Store' : 'Google Play');
      if (qrStore) qrStore.textContent = store === 'ios' ? 'App Store · iOS' : 'Google Play · Android';
      if (qrLink) qrLink.href = url;
      lastFocus = document.activeElement;
      modal.classList.remove('qr-modal--hidden');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const closeBtn = modal.querySelector('.qr-modal__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    function close() {
      modal.classList.add('qr-modal--hidden');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (qrImg) qrImg.src = '';
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    badges.forEach((badge) => {
      badge.addEventListener('click', (e) => {
        if (isCoarsePointer()) return; // mobile/touch -> let the native link open
        e.preventDefault();
        open(badge);
      });
    });

    modal.addEventListener('click', (e) => {
      if (e.target.matches('[data-qr-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('qr-modal--hidden')) close();
    });
  })();

})();

/* =====================================================
   MOTION P2 — Card 3D tilt + Magnetic CTA
   =====================================================
   Pilote les CSS vars --tilt-x/--tilt-y (sur .stat-card,
   .psv-card, .testi-card) et --magnet-x/--magnet-y (sur les
   CTAs primaires) à partir de la position du pointeur.

   Désactivé sur :
     - prefers-reduced-motion: reduce
     - pointer coarse (touch-first)
*/
(function initMotionP2() {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isCoarse = window.matchMedia('(pointer: coarse)').matches;
  if (prefersReduced || isCoarse) return;

  // ─── Card 3D tilt ─────────────────────────────────────
  /* USER FEEDBACK : la psv-card (Amelie B.) bouge encore trop avec 2deg.
     On la retire entierement de la cible tilt. Le hover ne fait plus que
     monter l'ombre (.psv-card:hover -> elev-2) — la carte reste fixe. */
  const TILT_TARGETS = '.stat-card, .testi-card';
  const TILT_MAX_DEG = 5;

  document.querySelectorAll(TILT_TARGETS).forEach((card) => {
    let raf = 0;
    let rect = null;
    const maxDeg = TILT_MAX_DEG;

    const onEnter = () => {
      rect = card.getBoundingClientRect();
      card.dataset.tilting = 'on';
    };

    const onMove = (e) => {
      if (!rect) rect = card.getBoundingClientRect();
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        // Position normalisée [-0.5, +0.5] dans la carte
        const nx = (e.clientX - rect.left) / rect.width - 0.5;
        const ny = (e.clientY - rect.top) / rect.height - 0.5;
        // rotateX inversé (haut = -, bas = +) pour effet naturel
        card.style.setProperty('--tilt-x', `${-ny * maxDeg}deg`);
        card.style.setProperty('--tilt-y', `${nx * maxDeg}deg`);
      });
    };

    const onLeave = () => {
      delete card.dataset.tilting;
      card.style.setProperty('--tilt-x', '0deg');
      card.style.setProperty('--tilt-y', '0deg');
      rect = null;
    };

    card.addEventListener('pointerenter', onEnter, { passive: true });
    card.addEventListener('pointermove', onMove, { passive: true });
    card.addEventListener('pointerleave', onLeave, { passive: true });
  });

  // ─── Magnetic CTA ─────────────────────────────────────
  const MAGNET_TARGETS = '.btn.primary, .hero-primary-cta, .signup-btn, .mission-cta__primary, .final-cta__primary, .contact-premium__primary';
  const MAGNET_RADIUS = 110;  // px — distance d'attraction
  const MAGNET_STRENGTH = 0.35; // 0-1 — combien le bouton suit (réduit, suivait trop le curseur)

  document.querySelectorAll(MAGNET_TARGETS).forEach((btn) => {
    let raf = 0;

    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist > MAGNET_RADIUS + Math.max(rect.width, rect.height) / 2) return;
        btn.dataset.magnet = 'on';
        btn.style.setProperty('--magnet-x', `${dx * MAGNET_STRENGTH}px`);
        btn.style.setProperty('--magnet-y', `${dy * MAGNET_STRENGTH}px`);
      });
    };

    const onLeave = () => {
      delete btn.dataset.magnet;
      btn.style.setProperty('--magnet-x', '0px');
      btn.style.setProperty('--magnet-y', '0px');
    };

    btn.addEventListener('pointermove', onMove, { passive: true });
    btn.addEventListener('pointerleave', onLeave, { passive: true });
  });
})();

/* =====================================================
   PROFILE-VALUE — Compteurs métriques candidat
   =====================================================
   Anime les 3 .psv-card__metric-value (4 ans, 98%, 240+) quand
   la carte entre en vue. Pattern aligné avec les counters #stats. */
(function initPsvCardMetrics() {
  const card = document.querySelector('.psv-card');
  if (!card || !('IntersectionObserver' in window)) return;

  const values = Array.from(card.querySelectorAll('.psv-card__metric-value[data-target]'));
  if (!values.length) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const DURATION = 1300;

  const formatValue = (v, decimals) => {
    const f = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals
    });
    return f.format(v);
  };

  const animateValue = (el) => {
    const target = parseFloat(el.dataset.target || '0');
    const suffix = el.dataset.suffix || '';
    const decimals = parseInt(el.dataset.decimals || '0', 10);
    const start = performance.now();
    const frame = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      const v = target * easeOut(t);
      el.innerHTML = formatValue(v, decimals) + suffix;
      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        el.innerHTML = formatValue(target, decimals) + suffix;
        el.closest('.psv-card')?.classList.add('psv-card--counted');
      }
    };
    requestAnimationFrame(frame);
  };

  if (prefersReduced) {
    values.forEach((el) => {
      const target = parseFloat(el.dataset.target || '0');
      const suffix = el.dataset.suffix || '';
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      el.innerHTML = formatValue(target, decimals) + suffix;
    });
    card.classList.add('psv-card--counted');
    return;
  }

  let started = false;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting && !started) {
        started = true;
        values.forEach((el) => animateValue(el));
        io.unobserve(card);
      }
    });
  }, { threshold: .35 });

  io.observe(card);
})();

/* =====================================================
   HOW IT WORKS — Timeline interactive (active step)
   =====================================================
   Détecte quel .how-step est au centre du viewport et lui pose
   .is-active. Les steps précédents reçoivent .is-visited. Met à
   jour --fill-pct sur le .how-flow parent pour que la ligne
   verticale se remplisse en or jusqu'à la hauteur du badge actif.
*/
(function initHowFlow() {
  const flows = document.querySelectorAll('.how-flow');
  if (!flows.length || !('IntersectionObserver' in window)) return;

  const mqMobile = window.matchMedia('(max-width: 768px)');

  flows.forEach((flow) => {
    const steps = Array.from(flow.querySelectorAll('.how-step'));
    if (!steps.length) return;

    let currentIdx = -1;
    let dotEls = [];

    const setActive = (idx, mobileMode) => {
      if (idx === currentIdx) return;
      currentIdx = idx;
      steps.forEach((s, i) => {
        s.classList.toggle('is-active', i === idx);
        // Visited a du sens en mode desktop (timeline progressive) ;
        // sur mobile carrousel, on ne marque rien comme visited.
        s.classList.toggle('is-visited', !mobileMode && i < idx);
      });
      // Met à jour --fill-pct uniquement en mode desktop.
      if (!mobileMode) {
        const fillPct = idx < 0 ? 0 : ((idx + 1) / steps.length) * 100;
        flow.style.setProperty('--fill-pct', `${fillPct}%`);
      }
      // Sync dots en mobile.
      if (dotEls.length) {
        dotEls.forEach((d, i) => {
          d.setAttribute('aria-current', i === idx ? 'true' : 'false');
        });
      }
    };

    // Map des ratios persistants entre callbacks IO. CRITIQUE : sans ça,
    // chaque batch d'entries écrasait l'état → seul le step qui vient de
    // changer comptait, les autres étaient considérés à 0 → activation
    // erratique sur cards 2-3.
    const desktopRatios = new Map();
    const mobileRatios = new Map();
    const STEP_THRESHOLDS = Array.from({ length: 11 }, (_, i) => i / 10);

    /* ─── DESKTOP : scroll-driven timeline (vertical) ────────────── */
    const desktopIO = new IntersectionObserver((entries) => {
      if (mqMobile.matches) return;  // mobile uses carousel IO instead
      entries.forEach((e) => {
        desktopRatios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0);
      });
      let bestIdx = -1;
      let bestRatio = 0;
      steps.forEach((step, i) => {
        const r = desktopRatios.get(step) || 0;
        if (r > bestRatio) {
          bestRatio = r;
          bestIdx = i;
        }
      });
      if (bestIdx >= 0) setActive(bestIdx, false);
    }, {
      root: null,
      rootMargin: '-35% 0px -35% 0px',
      threshold: STEP_THRESHOLDS
    });

    /* ─── MOBILE : carousel dots indicator + active sync ──────────── */
    let mobileIO = null;

    const buildMobileDots = () => {
      if (dotEls.length) return;  // already built
      const ul = document.createElement('ul');
      ul.className = 'how-flow-dots';
      ul.setAttribute('aria-label', 'Progression des étapes');
      steps.forEach((step, i) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'how-flow-dot';
        btn.setAttribute('aria-label', `Étape ${i + 1} sur ${steps.length}`);
        btn.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        btn.addEventListener('click', () => {
          step.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
        li.appendChild(btn);
        ul.appendChild(li);
      });
      // Insertion juste après .how-flow
      flow.parentNode.insertBefore(ul, flow.nextSibling);
      dotEls = Array.from(ul.querySelectorAll('.how-flow-dot'));
    };

    const enableMobileObserver = () => {
      if (mobileIO) return;
      mobileIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          mobileRatios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0);
        });
        let bestIdx = -1;
        let bestRatio = 0;
        steps.forEach((step, i) => {
          const r = mobileRatios.get(step) || 0;
          if (r > bestRatio) {
            bestRatio = r;
            bestIdx = i;
          }
        });
        if (bestIdx >= 0) setActive(bestIdx, true);
      }, {
        root: flow,
        threshold: STEP_THRESHOLDS
      });
      steps.forEach((s) => mobileIO.observe(s));
    };

    if (mqMobile.matches) {
      buildMobileDots();
      enableMobileObserver();
    } else {
      steps.forEach((s) => desktopIO.observe(s));
    }

    // Si l'utilisateur tourne son téléphone / resize, on rebascule.
    const onMqChange = (e) => {
      if (e.matches) {
        buildMobileDots();
        enableMobileObserver();
      } else if (mobileIO) {
        mobileIO.disconnect();
        mobileIO = null;
        steps.forEach((s) => desktopIO.observe(s));
      }
    };
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', onMqChange);
  });
})();

/* =====================================================
   SERVICE WORKER — enregistrement différé
   Permet : cache des assets statiques (stale-while-revalidate)
            + fallback offline minimal.
   On enregistre seulement après 'load' + whenIdle pour ne pas
   compétitionner avec le first paint sur mobile.
===================================================== */
(() => {
  if (!('serviceWorker' in navigator)) return;
  // Ne pas tenter sur localhost file:// ni domaines non-HTTPS (sauf localhost)
  if (location.protocol === 'file:') return;

  const register = () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .catch(() => { /* silencieux — pas critique */ });
  };

  if (document.readyState === 'complete') {
    (window.whenIdle || ((fn) => setTimeout(fn, 1000)))(register);
  } else {
    window.addEventListener('load', () => {
      (window.whenIdle || ((fn) => setTimeout(fn, 1000)))(register);
    }, { once: true });
  }
})();

/* =====================================================
   ANALYTICS — Google Analytics 4 + Consent Mode v2
   -----------------------------------------------------
   Conformité Loi 25 (Québec) / RGPD — trois verrous :

     1. gtag.js n'est JAMAIS téléchargé tant que le visiteur
        n'a pas coché « témoins statistiques ». Aucune requête
        ne part vers Google avant le clic.
     2. Consent Mode v2 est déclaré « denied » par défaut, donc
        même si un tag était ajouté plus tard (GTM, Ads), il
        démarre sans stockage ni personnalisation.
     3. track() est un no-op tant que le consentement n'est pas
        accordé, et `ga-disable-<ID>` coupe gtag à la racine si
        le visiteur retire son consentement.

   Le consentement vient de localStorage['cookie-consent'] écrit
   par initCookieConsent(), et se met à jour en direct via
   l'événement 'consent:change' (ou l'événement 'storage' quand
   le choix est fait dans un autre onglet).

   Debug : ajouter ?ga_debug=1 à l'URL — les events sont loggés
   en console (aucun envoi supplémentaire).

   Dimensions personnalisées à déclarer dans GA4
   (Admin → Définitions personnalisées → Portée « Événement ») :
     audience_mode  → cand | cli      (le segment regardé)
     site_lang      → fr | en
     color_theme    → light | dark
===================================================== */
(() => {
  'use strict';

  /* ---- Configuration --------------------------------
     ID de mesure GA4 : Admin → Flux de données → Web.
     Tant qu'il vaut le placeholder, le module ne fait rien.
  --------------------------------------------------- */
  const GA_MEASUREMENT_ID = 'G-GTFP62MP9G';

  const CONSENT_KEY = 'cookie-consent';
  const DEBUG = /[?&]ga_debug=1/.test(location.search);

  const idLooksReal = /^G-[A-Z0-9]{6,}$/i.test(GA_MEASUREMENT_ID)
    && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX';

  if (!idLooksReal) {
    if (DEBUG) console.warn('[analytics] GA_MEASUREMENT_ID non configuré — module inactif.');
    window.trackEvent = function () { };
    return;
  }

  /* ---- Socle gtag ----------------------------------- */
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;

  // Kill switch officiel de gtag.js : actif tant qu'on n'a pas le consentement.
  window['ga-disable-' + GA_MEASUREMENT_ID] = true;

  // Consent Mode v2 — tout refusé par défaut.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    personalization_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500
  });
  gtag('set', 'ads_data_redaction', true);
  gtag('set', 'url_passthrough', false);

  let gaLoaded = false;
  let granted = false;

  /* ---- Contexte joint à chaque event ----------------- */
  function context() {
    const root = document.documentElement;
    return {
      audience_mode: root.getAttribute('data-mode') || 'cand',
      site_lang: (window.I18N && window.I18N.lang)
        || root.getAttribute('data-lang') || root.lang || 'fr',
      color_theme: root.getAttribute('data-theme') || 'light'
    };
  }

  function loadGa() {
    if (gaLoaded) return;
    gaLoaded = true;

    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(GA_MEASUREMENT_ID);
    s.onerror = () => {
      gaLoaded = false;
      if (DEBUG) console.warn('[analytics] gtag.js bloqué (adblocker ou réseau).');
    };
    document.head.appendChild(s);

    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, Object.assign({
      // GA4 tronque l'IP par défaut ; on le déclare explicitement pour l'audit.
      anonymize_ip: true,
      send_page_view: true
    }, context()));

    if (DEBUG) console.info('[analytics] gtag.js chargé pour', GA_MEASUREMENT_ID);
  }

  /* ---- Application du consentement ------------------- */
  function applyConsent(consent) {
    const stats = !!(consent && consent.stats);
    const marketing = !!(consent && consent.marketing);

    window['ga-disable-' + GA_MEASUREMENT_ID] = !stats;

    gtag('consent', 'update', {
      analytics_storage: stats ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
      personalization_storage: marketing ? 'granted' : 'denied'
    });

    granted = stats;
    if (DEBUG) console.info('[analytics] consentement', { stats, marketing });

    if (stats) loadGa();
  }

  function readConsent() {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /* ---- API d'envoi ----------------------------------- */
  function track(name, params) {
    if (!granted || !name) return;
    const payload = Object.assign(context(), params || {});
    if (DEBUG) console.info('[analytics] →', name, payload);
    gtag('event', name, payload);
  }

  // Exposé pour le reste du site (et pour tester à la main en console).
  window.trackEvent = track;

  /* ---- Écoute du consentement ------------------------ */
  const existing = readConsent();
  if (existing) applyConsent(existing);

  document.addEventListener('consent:change', (e) => {
    applyConsent(e.detail || null);
    track('cookie_consent', {
      analytics_consent: !!(e.detail && e.detail.stats),
      marketing_consent: !!(e.detail && e.detail.marketing),
      consent_action: (e.detail && e.detail.source) || 'unknown'
    });
  });

  // Choix fait dans un autre onglet
  window.addEventListener('storage', (e) => {
    if (e.key !== CONSENT_KEY) return;
    applyConsent(readConsent());
  });

  /* =====================================================
     Événements — tout est branché même sans consentement :
     track() reste muet tant que le visiteur n'a pas accepté.
  ===================================================== */

  const STORE_BY_HOST = {
    'apps.apple.com': 'ios',
    'itunes.apple.com': 'ios',
    'play.google.com': 'android'
  };

  function linkLabel(el) {
    return (el.getAttribute('aria-label') || el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 100);
  }

  function handleLink(a) {
    const raw = a.getAttribute('href') || '';
    if (!raw || raw === '#') return;

    if (raw.startsWith('tel:')) {
      track('contact_click', { contact_method: 'phone', cta_id: a.dataset.analyticsCta || '' });
      return;
    }
    if (raw.startsWith('mailto:')) {
      track('contact_click', { contact_method: 'email', cta_id: a.dataset.analyticsCta || '' });
      return;
    }

    let url = null;
    try { url = new URL(raw, location.href); } catch { /* href exotique */ }

    // Téléchargement de l'app — la conversion principale du site.
    const store = a.dataset.store || (url && STORE_BY_HOST[url.hostname]);
    if (store) {
      track('app_store_click', { store, cta_id: a.dataset.analyticsCta || '' });
      return;
    }

    // CTA explicitement annotés dans le HTML (data-analytics-cta="…")
    if (a.dataset.analyticsCta) {
      track('cta_click', { cta_id: a.dataset.analyticsCta, link_url: url ? url.href : raw });
      return;
    }

    // Repli : inscription / connexion repérés par leur classe.
    if (a.classList.contains('signup-btn')) {
      track('cta_click', { cta_id: 'signup_generic', cta_label: linkLabel(a) });
      return;
    }
    if (a.classList.contains('login-btn')) {
      track('cta_click', { cta_id: 'login_generic', cta_label: linkLabel(a) });
      return;
    }

    if (!url) return;

    // Lien sortant (réseaux sociaux, ancienne plateforme, portail client…)
    if (url.origin !== location.origin) {
      track('outbound_click', {
        link_domain: url.hostname,
        link_url: url.href,
        cta_label: linkLabel(a)
      });
      return;
    }

    // Navigation interne par ancre (menu, footer)
    if (raw.startsWith('#')) {
      track('nav_click', { nav_target: raw.slice(1), cta_label: linkLabel(a) });
    }
  }

  document.addEventListener('click', (e) => {
    const target = (e.target && e.target.nodeType === 1) ? e.target : null;
    if (!target) return;

    const a = target.closest('a[href]');
    if (a) handleLink(a);

    const bubble = target.closest('#indBubbles .bubble');
    if (bubble) {
      track('industry_select', {
        industry: bubble.getAttribute('aria-label') || bubble.dataset.id || ''
      });
    }

    const faq = target.closest('.faq-q');
    if (faq) {
      // Capture : aria-expanded n'a pas encore été inversé par le handler du site.
      if (faq.getAttribute('aria-expanded') !== 'true') {
        track('faq_open', { faq_question: linkLabel(faq), faq_id: faq.id || '' });
      }
    }

    const cta = target.closest('button[data-analytics-cta]');
    if (cta) track('cta_click', { cta_id: cta.dataset.analyticsCta, cta_label: linkLabel(cta) });
  }, true);

  /* ---- Bascules candidat/client, langue, thème --------
     Ces trois événements sont aussi émis au chargement par le
     code d'init : on attend que la page soit posée avant de les
     considérer comme une action volontaire du visiteur.
  --------------------------------------------------- */
  let interactive = false;
  const markInteractive = () => { interactive = true; };
  if (document.readyState === 'complete') {
    setTimeout(markInteractive, 800);
  } else {
    window.addEventListener('load', () => setTimeout(markInteractive, 800), { once: true });
  }

  document.addEventListener('modechange', (e) => {
    if (!interactive) return;
    gtag('set', context());
    track('audience_switch', { mode: (e.detail && e.detail.mode) || '' });
  });

  document.addEventListener('i18n:change', (e) => {
    if (!interactive) return;
    gtag('set', context());
    track('language_switch', { language: (e.detail && e.detail.lang) || '' });
  });

  document.addEventListener('theme:change', (e) => {
    if (!interactive) return;
    gtag('set', context());
    track('theme_switch', { theme: (e.detail && e.detail.theme) || '' });
  });

  /* ---- Profondeur de lecture ------------------------- */
  window.whenIdle(function initScrollDepth() {
    const marks = [25, 50, 75, 90];
    const done = new Set();
    let ticking = false;

    function measure() {
      ticking = false;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      const pct = ((window.scrollY || doc.scrollTop) / scrollable) * 100;
      marks.forEach((m) => {
        if (pct >= m && !done.has(m)) {
          done.add(m);
          track('scroll_depth', { percent_scrolled: m });
        }
      });
      if (done.size === marks.length) {
        window.removeEventListener('scroll', onScroll);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  });

  /* ---- Sections réellement vues ---------------------- */
  window.whenIdle(function initSectionViews() {
    if (!('IntersectionObserver' in window)) return;

    const sections = document.querySelectorAll('main section[id], main .section[id]');
    if (!sections.length) return;

    // rootMargin plutôt que threshold : une section plus haute que l'écran
    // n'atteint jamais un ratio de 0.5. Ici on déclenche quand la section
    // traverse la bande centrale de l'écran — vrai quelle que soit sa hauteur.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        track('section_view', { section_id: entry.target.id });
      });
    }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });

    sections.forEach((s) => io.observe(s));
  });
})();


